// ── 목업 데이터 (추후 WebSocket으로 STS/Java 백엔드에서 수신) ──
import type {
  NavItem, Product, Solution, NewsItem, Instrument, FooterColumn, CompanyInfo,
} from '@/types';

// 제품별 SVG (Vite url import — 거의 모든 이미지형 개체는 SVG)
import currentSvg from '@svg/prod-current.svg';
import dpSvg from '@svg/prod-dp.svg';
import tempSvg from '@svg/prod-temp.svg';
import phSvg from '@svg/prod-ph.svg';
import gatewaySvg from '@svg/prod-gateway.svg';
import wbgtSvg from '@svg/prod-wbgt.svg';
import wbgtLcdSvg from '@svg/solution-wbgt.svg';

// ── D안 상단 메뉴(2026-09, Phase 1): 제품 · 안전 · 환경 · 도입사례 · 고객지원 · 회사소개(맨끝·흐리게) ──
//   철학: 제품 최우선 / 회사소개 후순위(dim). 대분류=안전·환경 도메인.
//   ★새 메뉴(안전·환경·도입사례)는 아직 "전용 페이지"가 없음 → 최상위 to 미지정. 자식도 페이지 있는 것만 to 연결,
//     나머지는 '#'(미이동). 실제 페이지+라우트는 Phase 3에서 신설/연결. (지금 클릭해도 안 깨지게)
//   기존 페이지 재활용: 제품→/products, 환경 하위 제조/설치→/business/igm·/business/install, 고객지원→/support, 회사소개→/company.
export const NAV: NavItem[] = [
  // 제품(최우선) — /products 통합 카탈로그. 서브메뉴 = 카탈로그 '제품 유형' 필터 축과 1:1(게이트웨이/계측기/안전단말기).
  // [2026-09-08 사용자] 과거 데이터사이트 기준 라벨 폐기 → 신 카탈로그 유형축으로 재구조. UI는 심플·절제.
  //   색상은 오직 도메인 '구분자(dot)'로만(환경=그린·안전=시안). to=/products?type=… → 카탈로그가 쿼리로 초기 필터.
  //   유틸 2종(스펙표·PDF)은 sep(옅은 구분선)으로 유형 그룹과 분리. 아직 미구현('#').
  // [2026-10-08 사용자] 서브메뉴 '이름·순서'를 기획서 v7 §1 전체메뉴트리대로 정렬(저위험=페이지 1:1 존재).
  //   ※ 설치안내 재정의·환경 편입(제조/설치사업/시설안내)만 구조변경이라 부장 확정 후(B). 그 외는 문서대로.
  { label: '제품', href: '#', to: '/products', children: [
    { label: '전체 제품', href: '#', to: '/products' },
    { label: 'EVS-G200 게이트웨이', href: '#', to: '/products/evs-g200', dot: 'environment' },
    { label: '환경 계측기 4종', href: '#', to: '/products/instruments', dot: 'environment' },
    { label: 'AI IoT 체감온도계', href: '#', to: '/products/heat-index', dot: 'safety' },
    { label: '모니터·TV 연결 중계기', href: '#', to: '/products/display-repeater', dot: 'safety' },
  ]},
  // 안전(산업안전보건) — /safety 기본 페이지 + 앵커 섹션(2026-09-08 재편, 제품 우선).
  //   ★제품 우선: '안전 제품'을 맨 위 → SaaS·체험 등 부차 항목은 sep 아래. to '/safety#id' = DomainPage topics[].id.
  // [2026-10-08] /safety를 문서 섹션순서로 재구성(앵커 섹션 폐지)하면서, 구 '/safety#id' 앵커가 깨짐 →
  //   서브메뉴 '라벨/구조는 유지'하되 타깃만 신규 독립 페이지로 재와이어링(A 와이어링. 메뉴트리 재편=B는 부장확정 후).
  { label: '안전', href: '#', to: '/safety', children: [
    { label: '안전 솔루션', href: '#', to: '/safety' },
    { label: '체감온도 통합관제 플랫폼', href: '#', to: '/safety/platform' },
    { label: '온열질환 예방', href: '#', to: '/safety/heat' }, // [2026-10-11] 기획서 v7 3-3 독립 안전페이지로 연결(구 제품페이지 연결에서 변경). 제품은 그 페이지 안에서 링크
    { label: '한랭작업 관리', href: '#', to: '/safety/cold' },
    { label: '밀폐공간 가스안전', href: '#', to: '/safety/gas' },
  ]},
  // 환경(대기환경) — /environment '제품 위주'(2026-09-08 사용자 결정, koreadigital식). 규제/정보는 '설치안내'로 분리.
  // [2026-10-08 B안/부장확정] 기획서 v7 §1 환경 메뉴트리대로: 솔루션·게이트웨이제조·설치사업·시설안내·GreenLink.
  { label: '환경', href: '#', to: '/environment', children: [
    { label: '환경 IoT 솔루션', href: '#', to: '/environment' },
    { label: 'IoT 게이트웨이 제조', href: '#', to: '/business/igm' },
    { label: 'IoT 설치사업', href: '#', to: '/business/install' },
    { label: '배출·방지시설 안내', href: '#', to: '/guide' },
    { label: 'GreenLink 전송', href: '#', to: '/environment/greenlink' },
  ]},
  // 설치안내(규제/정보) — 신규 별도 메뉴+페이지(2026-09-08). '제품' 아닌 규제/설치 정보 분리(필요한 사람만 열람).
  //   원본=evstech 실측(방지시설 정의/배출시설/설치대상). 배경 white([[evs-page-background-scheme]]).
  //   [2026-09-08] 설치안내 = 안전/환경 탭 분할(/guide?tab=…). 도메인 dot(안전=시안/환경=그린)로 구분.
  // [2026-10-08 B안/부장확정] 설치안내 = '도입 프로세스'로 재정의(기획서 v7 §1·5). 구 시설/계측 콘텐츠는 /guide(환경 하위)로 일원화(보존).
  { label: '설치안내', href: '#', to: '/install/process', children: [
    { label: '도입 절차', href: '#', to: '/install/process' },
    { label: '현장실사·준비자료', href: '#', to: '/install/site-survey' },
    { label: '설치 후 운영', href: '#', to: '/install/operation' },
  ]},
  // 설치 사례(도입사례) — [2026-09-09 사용자] 신규 /cases 페이지 연결. 안전/환경 탭.
  { label: '설치 사례', href: '#', to: '/cases', children: [
    { label: '환경 설치사례', href: '#', to: '/cases?tab=environment', dot: 'environment' },
    { label: '안전 설치사례', href: '#', to: '/cases?tab=safety', dot: 'safety' },
    { label: 'A/S·개선사례', href: '#', to: '/cases?tab=as', dot: 'environment' }, // [2026-10-11] 기획서 v7 §6-1 A/S 탭 분리(구 tab=environment→'환경'으로 오표기되던 문제 교정)
    { label: '주요 거래처·실적', href: '#', to: '/cases/clients', sep: true },
  ]},
  // 고객지원 — 기존 /support 재활용
  { label: '고객지원', href: '#', to: '/support', children: [
    { label: '실시간 상담', href: '#', to: '/support/consult' },
    // [2026-09-10] 견적·설치협력·사후관리 문의 → 통합 '문의하기'(카테고리로) + '내 문의' 신설.
    { label: '문의하기', href: '#', to: '/support/inquiry' },
    { label: '내 문의', href: '#', to: '/support/my-inquiries' },
    // [2026-09-12] A/S 전용 모듈(문의와 분리) — 접수→완료 상태추적. 허브=/support/as(공개 안내·절차 + 신청/현황 진입).
    { label: 'A/S 신청·현황', href: '#', to: '/support/as', sep: true },
    { label: 'FAQ', href: '#', to: '/support/faq' },
    { label: '자료실', href: '#', to: '/support/resources' },
    { label: '뉴스·소식', href: '#', to: '/news' },
  ]},
  // 회사소개 — 맨 끝 · 흐리게(dim). 기존 /company 재활용
  { label: '회사소개', href: '#', to: '/company', children: [
    // 기획서 v7 §1: 인사말·회사개요 / 연혁·조직도 / 인증·기술·연구소 / 오시는 길 (독립 URL 연결)
    { label: '인사말·회사개요', href: '#', to: '/company' },
    { label: '연혁·조직도', href: '#', to: '/company/history' },
    { label: '인증·기술·연구소', href: '#', to: '/company/certifications' },
    { label: '오시는 길', href: '#', to: '/company/location' },
  ]},
];

export const NAV_UTIL = ['회원가입', '사이트맵'];

// 히어로
export const HERO = {
  headline: 'IoT로 환경대기오염을 실시간 모니터링하다',
  // evstech.co.kr(융합 대상) 서브 슬로건 — 헤드라인 아래 구별 표기
  slogan: '맑은 하늘을 위한 한 걸음, IoT Gateway로 시작하세요.',
  support: '제조부터 설치 및 A/S까지, 완벽한 IoT 토탈 솔루션',
  cta: '자세히 보기',
};

// 이중 진입(D안 Phase 2-① 신규) — Hero 아래. 방문자가 '제품 축' / '안전·환경(도메인) 축' 두 갈래로 진입.
//   문구는 1차안(구조 우선, 추후 다듬기). ★링크는 Phase 1 라우트 재편 시 연결 → 현재는 미연결('#').
// [2026-09-08 사용자] 2카드→3카드(제품/안전/환경). 안전·환경 두 축을 각각 카드로 분리해 도메인 진입을 명확히.
//   연결: 제품→/products(도메인 섹션) · 안전→/safety · 환경→/environment (세 기본 페이지, 앵커 섹션 구조).
// ── 전 페이지 공통 히어로 이미지 (단일 소스, 2026-09-08 사용자 확정) ──────────────────────
//   사용자: "제품 히어로 이미지 맘에 든다 → 다른 페이지도 일단 이걸로 통일. 차후 변경 가능하게."
//   ★변경 방법: 아래 한 값만 수정하면 /products·/safety·/environment 등 이 상수를 쓰는 전 페이지에 반영.
//   ⚠ 현재 이미지(products-hero.webp) 원본에 'Unsplash+' 워터마크 존재 → 정식 이미지 확보 시 이 값 교체(우회 안 함).
export const PAGE_HERO_IMAGE = '/images/products/products-hero.webp';

export const DUAL_ENTRY = {
  title: '원하는 방식으로 바로 찾기',
  cards: [
    { key: 'product', label: '제품으로 찾기', desc: '게이트웨이 · 계측기 · 안전 단말기', to: '/products' },
    { key: 'safety', label: '안전으로 찾기', desc: '산업안전보건 · 온열질환', to: '/safety' },
    { key: 'environment', label: '환경으로 찾기', desc: '대기환경 · 방지시설', to: '/environment' },
  ],
};

// 실시간 상담 위젯(D안 Phase 2-③, 신규) — 전 페이지 우하단 상시. 웹 상담 위젯①.
//   상담원 부재 시 자동응답 → 이메일·카톡 전환 안내(문구만; 실제 연락처·챗봇 연동은 Phase 6).
export const CHAT_WIDGET = {
  launcherLabel: '실시간 상담',
  title: 'EVS 상담',
  greeting: '무엇을 도와드릴까요? 제품·견적 문의 환영합니다.',
  awayNotice: '상담원 부재 시 남겨주시면 이메일·카카오톡으로 답변드립니다.',
  actions: [
    { label: '견적문의', to: '/support#inquiry' },
    { label: '카카오톡 상담', to: '#' },
    { label: '자주 묻는 질문(FAQ)', to: '#' },
  ],
};

// 회사소개 짧은 티저(D안 Phase 2-④, 신규) — 홈 하단(오시는 길 위). 후순위라 아주 짧게 + /company 진입.
export const COMPANY_TEASER = {
  eyebrow: '회사소개',
  headline: '맑은 하늘을 만드는 IoT 기업, EVS',
  desc: '주식회사 이브이에스는 제조부터 설치·A/S까지, 대기환경과 산업안전을 아우르는 IoT 토탈 솔루션을 제공합니다.',
  cta: '회사소개 자세히 보기',
  to: '/company', // [2026-09-09 사용자] 홈에서 회사소개로 이동(SPA).
};

// 안전/환경 도메인 요약(D안 Phase 2-④, 신규) — 홈에서 두 도메인 핵심 하이라이트 + 페이지 진입.
//   홈=집합체 원칙: 각 도메인의 '요약'만, 상세는 각 페이지(Phase 3). ★링크 미연결('#') → /safety·/environment.
// [2026-09-09 사용자] 첫 방문자에게 강한 인상 = EVS 정체성 요약. 다크 프리미엄 + 배경 가로 100% 분할(안전=blue/환경=green).
//   문구 확정("이런 문구 좋아"): eyebrow + 2줄 타이틀 + 서브카피. points=상태 뱃지 동반. cta/altCta 클릭 이동 실연결.
//   ★[2026-09-09 사용자] '표/틀' 금지 → 슬로건·철학 표현. 마스터 한 줄 + 각 도메인 자기 슬로건(제목+철학) 한 쌍(의미 맞춤).
export const DOMAIN_SUMMARY = {
  eyebrow: 'TWO AXES, ONE PLATFORM',
  title: '안전과 환경, 두 축으로 현장을 지킵니다',
  domains: [
    {
      key: 'safety', no: '01', tag: '안전 · 산업안전보건',
      slogan: '사람의 안전을,\n현장에서 지킵니다',
      sub: '온열질환·밀폐가스 위험을 자체 SaaS로 실시간 관제해, 모든 근로자가 안전하게 하루를 마치도록 합니다.',
      points: [
        { label: '통합 관제 SaaS', badge: '운영중' },
        { label: '체감온도계 (WBGT)', badge: '신규' },
        { label: '가스검침', badge: '예정' },
      ],
      cta: '안전 자세히 보기', to: '/safety', demo: true, // demo=SaaS 데모(TrialModal)
    },
    {
      key: 'environment', no: '02', tag: '환경 · 대기환경',
      slogan: '맑은 대기를,\n데이터로 지킵니다',
      sub: '소규모 대기배출을 IoT로 정밀 측정하고 환경부 GreenLink로 전송해, 보이지 않는 대기까지 지킵니다.',
      points: [
        { label: 'IoT Gateway (EVS-G200)', badge: '구축완료' },
        { label: '방지시설 · 계측기', badge: '6종·4종' },
        { label: '환경부 GreenLink 전송', badge: '국가기관' },
      ],
      cta: '환경 자세히 보기', to: '/environment', altCta: '법규 대응 안내', altTo: '/guide?tab=environment',
    },
  ],
};

// 주력 제품(스포트라이트) — D안 '① 주력 제품'은 두 도메인 대표 제품을 함께 노출하도록 설계됨.
//   기존 EVS-G200 단독 → dual 재구성(2026-09-05, 사용자 확정). 안전 대표=체감온도계(D안 '안전 단말기' 갭 보강) / 환경 대표=EVS-G200.
//   행 교대 배치. 안전 이미지는 임시(블로그 실측컷 news-02) → 깨끗한 제품컷 Phase6 교체.
export const SPOTLIGHT = {
  sectionTitle: '주력 제품',
  intro: '안전과 환경, 두 도메인을 대표하는 IoT 제품입니다.',
  products: [
    {
      key: 'safety', domainTag: '안전', domainLabel: '산업안전보건',
      name: 'AI 온열질환 IoT 체감온도계',
      to: '/products/heat-index', // [2026-10-08 사용자] 홈 주력제품 클릭 → 제품 '상세페이지'로(기존 /safety 랜딩에서 변경). 뒤로가기=스크롤 복원.
      image: '/images/products/wbgt.png', imgFit: 'cover', // 안전 대표=체감온도계(Gemini 원본, 사용자 선택 2026-09-05)
      subtitle: '실시간 체감온도(WBGT) 관제 단말',
      description:
        '현장 체감온도(WBGT)를 실시간 산출해 폭염·온열질환 위험을 SaaS로 관제하는 IoT 단말입니다. 4채널 앱·SMS 즉시 알림, 관심·주의·경고·위험 4단계(체감온도 31·33·35·38℃) 자동 대응, 법정기록 자동 저장으로 고용노동부 체감온도 33도 휴식 의무에 대응합니다.',
      cta: '체감온도계 자세히',
    },
    {
      key: 'environment', domainTag: '환경', domainLabel: '대기환경',
      name: 'EVS-G200',
      to: '/products/evs-g200', // [2026-10-08 사용자] 홈 주력제품 클릭 → 제품 '상세페이지'로(기존 /environment 랜딩에서 변경).
      // [2026-10-08 사용자] AI 보강샷(gateway-v2.avif) → 실제 EVS-G200 제품샷으로 교체(환경부·한국환경공단 로고 함체).
      //   원본=DATAs/KakaoTalk_20260930_132329704.png(800×798) → 제품 중요부위 정사각 포커싱 크롭(흰 배경 유지, 무손실)=gateway-g200.png.
      //   [2026-10-08 사용자] 함체 본체에 타이트한 세로비 크롭(비율유지·중앙·케이블 제외) → 세로 카드(mediaTall 3:4)에
      //   contain으로 꽉 차게(크롭 없이 '최대한 크게'). 방식 근거=[[evs-image-box-design]].
      image: '/images/products/gateway-g200.png', imgFit: 'contain',
      subtitle: 'IoT 센서·게이트웨이 일체형 대기환경 측정기',
      description:
        'IoT 센서와 게이트웨이를 하나로 통합한 대기환경 실시간 측정 장치입니다. 유·무선 통신과 디스플레이를 갖춰 현장 설치·운영이 간편하며, 환경부·한국환경공단의 승인·확인을 거친 기술로 대기오염을 정밀 모니터링해 환경부 GreenLink로 전송합니다.',
      cta: '제품 자세히 보기',
    },
  ],
};

// IoT 계측기 · 단말기 — D안 '① 주력 제품·계측기'의 제품 축(환경 계측기 + 안전 단말기 두 그룹).
export const INSTRUMENTS_SECTION = {
  title: 'IoT 계측기 · 단말기',
  desc: '체감온도계·가스검침 단말기(안전 산업안전보건)부터 전류·차압·온도·pH 계측기(환경 대기환경 방지시설)까지, 현장 데이터를 실시간으로 정밀 측정합니다.',
  cta: '제품 전체 보기',
};
// IoT 정밀 계측기기 4종 — 실제 제품 사진(evstech 실측 매핑: product-1~4) + 원문 기반 설명.
export const INSTRUMENTS: Instrument[] = [
  // [2026-09-06 사용자 확정 스타일] 계측기도 체감온도계 'B안'과 동일 — 제품 중요부위만 정사각 포커싱 크롭(*-f)
  //   + 풀블리드(fill). 이미지마다 배경색/드롭섀도가 달라도 '제품을 최대한 크게' 포커싱하는 방향으로 통일.
  //   원본 배경 그대로 유지(재인코딩 크롭만). 상세 철학=[[evs-image-box-design]] / ROADMAP-D.
  { id: 'i1', label: '전류계', image: '/images/products/product-1-f.jpg', fill: true, desc: '분할형 CT 센서로 송풍·배출 전류를 측정해 방지시설 가동 상태를 감지합니다.' },
  { id: 'i2', label: '차압계', image: '/images/products/product-2-f.jpg', fill: true, desc: '집진·여과 시설의 전후단 압력차(Pa)를 정밀 측정합니다.' },
  { id: 'i3', label: '온도계', image: '/images/products/product-3-f.jpg', fill: true, desc: 'RTD 방식 온도센서로 배출가스·설비의 온도를 계측합니다.' },
  { id: 'i4', label: 'pH계', image: '/images/products/product-4-f.jpg', fill: true, desc: '흡수·세정 시설의 수소이온농도(pH)를 측정합니다.' },
];
// 안전 단말기(D안 ① 안전 축) — 체감온도계(있음) + 가스검침기(예정, 자리 예약). 사용자 지시 2026-09-05.
//   ※가스검침은 아직 제품/사진 없음 → soon:true 로 '예정' 플레이스홀더 카드(미리 자리 확보).
// ============================================================================
//  통합 제품 카탈로그 (/products '제품으로 찾기' — 2026-09-08 사용자, koreadigital 구조 참고[[evs-ref-koreadigital-structure]])
//  3축 facet: ① domain(안전/환경) ② type(게이트웨이/계측기/안전단말기 = 필터 기준) ③ specs(사양 불릿).
//  필터=제품 유형(기술 축), 카드=도메인 태그 병기 + 사양 중심. 검색은 향후 제품 증가 대비.
//  데이터 원본=evstech 실측([[evs-content-source-of-truth]]). anchor=상세 섹션 id(같은 페이지 스크롤).
// ============================================================================
export type ProductDomain = 'safety' | 'environment';
export type ProductType = 'gateway' | 'instrument' | 'terminal';
export interface CatalogProduct {
  id: string; name: string; domain: ProductDomain; type: ProductType; typeLabel: string;
  image?: string; soon?: boolean; desc: string; specs: string[]; anchor?: string;
}
// [2026-09-08 사용자] 우선순위 = 체감온도계(안전 단말기). 회사 대표제품이므로 '전체' 다음 최우선 탭.
//   그 뒤 환경 제품(게이트웨이·계측기). cf. [[evs-safety-wbgt-product]].
export const PRODUCT_FILTERS: { key: ProductType | 'all'; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'terminal', label: '안전 단말기' },
  { key: 'gateway', label: '게이트웨이' },
  { key: 'instrument', label: '계측기' },
];
// [2026-09-08 사용자] 배열 순서 = 카탈로그 '전체' 뷰 노출 순서. 우선순위=체감온도계(안전 대표제품) → 맨 앞.
//   그 뒤 가스검침기 → 환경 게이트웨이(EVS-G200) → 환경 계측기 4종. EVS-G200은 절대 체감온도계보다 위 아님.
export const PRODUCTS_CATALOG: CatalogProduct[] = [
  { id: 't1', name: 'AI 온열질환 IoT 체감온도계', domain: 'safety', type: 'terminal', typeLabel: '안전 단말기',
    // [2026-09-11] anchor=이 제품의 /products 내 상세 블록 id(p-t1). 카탈로그 카드·도메인페이지 카드 모두 이 블록으로.
    image: '/images/products/wbgt-cluster.png', anchor: 'p-t1',
    desc: '실시간 체감온도(WBGT)로 폭염·온열질환 위험을 SaaS로 관제합니다.',
    specs: ['체감온도(WBGT) 산출', '4단계 위험 경보', 'SaaS 통합관제'] },
  { id: 't2', name: '가스검침기', domain: 'safety', type: 'terminal', typeLabel: '안전 단말기', soon: true, anchor: 'p-t2',
    desc: '밀폐공간 유해가스 실시간 감지 — SaaS 통합관제 편입 예정.',
    specs: ['유해가스 실시간 감지', 'SaaS 편입 예정'] },
  { id: 'g200', name: 'IoT Gateway EVS-G200', domain: 'environment', type: 'gateway', typeLabel: '게이트웨이',
    image: '/images/products/gateway-g200.png', anchor: 'gateway', // [2026-10-08 사용자] 실제 EVS-G200 제품샷으로 교체(구 gateway-v2.avif). 정사각 포커싱 크롭.
    desc: 'IoT 센서·게이트웨이 일체형 대기환경 측정기. 유·무선 통신으로 현장 데이터를 수집·전송합니다.',
    specs: ['통신 유선·WiFi·LTE', '한국환경공단 승인·확인', '환경부 GreenLink 전송'] },
  { id: 'i1', name: '전류계', domain: 'environment', type: 'instrument', typeLabel: '계측기',
    image: '/images/products/product-1-f.jpg', anchor: 'p-i1',
    desc: '분할형 CT 센서로 송풍·배출 전류를 측정해 방지시설 가동 상태를 감지합니다.',
    specs: ['분할형 CT 센서', 'DC 4-20mA', '방지시설 가동 감지'] },
  { id: 'i2', name: '차압계', domain: 'environment', type: 'instrument', typeLabel: '계측기',
    image: '/images/products/product-2-f.jpg', anchor: 'p-i2',
    desc: '집진·여과 시설의 전후단 압력차(Pa)를 정밀 측정합니다.',
    specs: ['전후단 압력차(Pa)', '집진·여과 시설', 'RS-485'] },
  { id: 'i3', name: '온도계', domain: 'environment', type: 'instrument', typeLabel: '계측기',
    image: '/images/products/product-3-f.jpg', anchor: 'p-i3',
    desc: 'RTD 방식 온도센서로 배출가스·설비의 온도를 계측합니다.',
    specs: ['RTD 방식', '배출가스·설비 온도', '유입구 설치'] },
  { id: 'i4', name: 'pH계', domain: 'environment', type: 'instrument', typeLabel: '계측기',
    image: '/images/products/product-4-f.jpg', anchor: 'p-i4',
    desc: '흡수·세정 시설의 수소이온농도(pH)를 측정합니다.',
    specs: ['pH / ORP 측정', '흡수·세정 시설'] },
];

// [2026-09-12 사용자] 제품 A/S 처리 유형 — 설치형(FIELD=현장 출동 수리) / 휴대형(PORTABLE=입고 수리).
//   ★근거: A/S 처리 단계를 가르는 진짜 축은 도메인(안전/환경)이 아니라 '처리 방식'(설치형/휴대형).
//   ★현재 값은 Claude 잠정 판단(회사 미확정) — 회사 확정 시 ★이 맵 한 곳만★ 갱신하면 됨.
//     체감온도계(t1)=설치형 확정(사용자, 2026-09-12: 고정 IoT 단말·유선전원·LCD·SaaS 관제, 도입=현장 설치).
//     대부분 고정 IoT/유선 계측=설치형. 밀폐공간 가스검침기(t2)만 휴대형으로 잠정.
//   연관: AsForm(신청 폼 SELECT 표기), 차후 asMeta 단계 라벨을 도메인 대신 이 유형으로 분기 예정.
export type AsServiceType = 'FIELD' | 'PORTABLE';
export const PRODUCT_SERVICE_TYPE: Record<string, AsServiceType> = {
  t1: 'FIELD', t2: 'PORTABLE', g200: 'FIELD', i1: 'FIELD', i2: 'FIELD', i3: 'FIELD', i4: 'FIELD',
};
export const SERVICE_TYPE_LABEL: Record<AsServiceType, string> = { FIELD: '설치형', PORTABLE: '휴대형' };
export const serviceTypeOf = (id: string): AsServiceType => PRODUCT_SERVICE_TYPE[id] || 'FIELD';

// ============================================================================
// [2026-09-11 사용자] 제품 상세 보강(설계 A) — /products 각 제품 상세 블록의 풍부한 설명 + 플랫 SVG 피처.
//   ★"제품이 이 사이트의 핵심"(사용자). 카탈로그 카드 클릭 → 같은 페이지 이 상세로 스크롤(순환 제거).
//   근거: 전부 evstech 실측/기존 큐레이션 사양(G200 사양 verbatim·계측기 측정대상/방식·체감온도계 WBGT·4단계).
//         ★신규 스펙 날조 금지 — 여기 문구는 기존 확인된 사실의 재서술일 뿐. 사양 추가 시 evstech 재실측 후.
//   icon 키 = Products.tsx의 DETAIL_ICON 맵과 1:1(플랫 라인 SVG). id = PRODUCTS_CATALOG.id.
export interface ProductFeature { icon: string; title: string; desc: string; }
// [2026-09-11 사용자] specs = 공통 하드웨어 스펙표(게이트웨이처럼). 근거 있는 제품만 채움(없으면 생략 → "사양 준비중").
export interface ProductDetail { blurb: string; features: ProductFeature[]; specs?: { k: string; v: string }[]; }
// [2026-10-08] 제품 카탈로그 id → 독립 상세페이지 라우트(기획서 v7). Products·DomainPage 공유.
//   미매핑(t2 가스검침 등)=상세 미생성 → 카드는 비이동(예정) 또는 도메인 폴백.
export const PRODUCT_DETAIL_ROUTE: Record<string, string> = {
  g200: '/products/evs-g200',
  i1: '/products/instruments', i2: '/products/instruments', i3: '/products/instruments', i4: '/products/instruments',
  t1: '/products/heat-index',
};

export const PRODUCT_DETAILS: Record<string, ProductDetail> = {
  // ── 안전 단말기 ──
  t1: {
    blurb: '현장 체감온도(WBGT)를 실시간 산출해 폭염·온열질환 위험을 관제하는 AI IoT 단말입니다. 관심·주의·경고·위험 4단계(체감온도 31·33·35·38℃)를 자동 판정하고 4채널 앱·SMS로 즉시 알리며, 측정·경보 이력을 법정기록으로 자동 저장해 고용노동부 「체감온도 33℃ 이상 시 휴식」 의무 대응과 통합 관제 SaaS 연동을 지원합니다.',
    features: [
      { icon: 'wbgt', title: '체감온도(WBGT) 실시간 산출', desc: '현장 온·습도 기반 체감온도 자동 계산' },
      { icon: 'alert', title: '4단계 위험 경보', desc: '관심·주의·경고·위험(31·33·35·38℃) 자동 판정' },
      { icon: 'bell', title: '4채널 앱·SMS 즉시 알림', desc: '현장·관리자 다중 채널 위험 통지' },
      { icon: 'record', title: '법정기록 자동 저장', desc: '고용노동부 휴식 의무 대응 기록 보존' },
      { icon: 'cloud', title: 'SaaS 통합관제 연동', desc: '여러 현장을 한 화면에서 원격 관제' },
    ],
    // 하드웨어 스펙(실측: guide 안전 wbgt product.specs와 동일 — LCD 데이터시트/검증본). 기능성 항목은 위 features로.
    specs: [
      { k: '센서 정확도', v: '±0.5℃ 이내 (초정밀 센서)' },
      { k: '전송 주기', v: '1분 주기 클라우드 전송' },
      { k: '디스플레이', v: '4.3" TFT LCD · 480×272 · 262K색' },
      { k: '제어부', v: 'DWIN T5L0 ASIC · DGUS II · 8MB Flash' },
      { k: '밝기', v: '300nit LED 백라이트 (수명 20,000h↑)' },
      { k: '통신', v: 'WiFi · LTE · Ethernet' },
      { k: '동작 환경', v: '-20 ~ 70℃ · 습도 10~90%RH' },
      { k: '전원', v: 'DC 5V (정격 5W 이하)' },
    ],
  },
  t2: {
    blurb: '밀폐공간의 유해가스를 실시간 감지하는 IoT 단말을 준비하고 있습니다. 체감온도계와 동일하게 통합 관제 SaaS에 편입되어, 밀폐공간 작업의 가스 위험을 한 화면에서 관제할 수 있도록 개발 중입니다.',
    features: [
      { icon: 'gas', title: '유해가스 실시간 감지', desc: '밀폐공간 작업 안전을 위한 상시 측정' },
      { icon: 'cloud', title: 'SaaS 통합관제 편입 예정', desc: '체감온도계와 동일 관제 체계로 확장' },
    ],
  },
  // ── 환경: 게이트웨이 ──
  g200: {
    blurb: '계측기기의 데이터를 수집·표시하고 광대역(Ethernet·LTE)과 근거리(RS-485·232) 통신으로 관제센터에 실시간 전송하는 IoT 게이트웨이입니다. 7인치 터치 LCD로 현장에서 측정값을 즉시 확인하며, 단수형(G200)·복수형(G200D)·교대형 전 모델이 한국환경공단 승인·확인을 완료했습니다.',
    features: [
      { icon: 'comm', title: '광대역·근거리 통신', desc: 'Ethernet·LTE(VPN) + RS-485·RS-232' },
      { icon: 'gauge', title: '4~20mA 계측 연동', desc: '전류·차압·pH 10CH + 온도 1CH + RS-485 확장(55CH)' },
      { icon: 'screen', title: '7인치 터치 LCD', desc: '1080P 화면으로 현장 측정값 즉시 확인' },
      { icon: 'cert', title: '한국환경공단 승인·확인', desc: '단수·복수·교대형 전 모델 승인·확인 완료' },
    ],
  },
  // ── 환경: 계측기 4종 ──
  i1: {
    blurb: '분할형 CT 센서로 송풍기·배출설비의 전류를 측정해 방지시설의 가동 상태를 실시간 감지합니다. DC 4~20mA로 게이트웨이(EVS-G200)와 연동됩니다.',
    features: [
      { icon: 'current', title: '분할형 CT 센서', desc: '설비 정지 없이 전류 측정' },
      { icon: 'gauge', title: 'DC 4~20mA 출력', desc: '게이트웨이 표준 연동' },
      { icon: 'check', title: '방지시설 가동 감지', desc: '송풍·배출 가동 여부 판정' },
    ],
    // 검증 사실만(제품 이미지·evstech 맥락). 브랜드 무표기 범용 분할형 CT → 상세 전기사양은 데이터시트 필요.
    specs: [
      { k: '측정 대상', v: '송풍·배출설비 전류' },
      { k: '센서 방식', v: '분할형 CT (설비 정지 없이 설치)' },
      { k: '출력', v: 'DC 4~20mA' },
      { k: '게이트웨이 연동', v: 'EVS-G200 (10CH 전류센서 입력)' },
    ],
  },
  i2: {
    blurb: '집진·여과 시설의 전후단 압력차(Pa)를 정밀 측정해 필터 막힘·이상을 조기에 파악합니다. RS-485로 게이트웨이와 연동됩니다.',
    features: [
      { icon: 'pressure', title: '전후단 압력차(Pa)', desc: '집진·여과 시설 차압 측정' },
      { icon: 'comm', title: 'RS-485 통신', desc: '게이트웨이 연동' },
      { icon: 'check', title: '필터 상태 감시', desc: '막힘·이상 조기 파악' },
    ],
    // 브랜드=Sensys(제품 이미지 인쇄 확인). 정확 모델 미확인 → 검증 사실만. 상세 전기사양은 데이터시트 필요.
    specs: [
      { k: '측정 대상', v: '전후단 압력차 (Pa)' },
      { k: '표시', v: '전면 LCD (Pa)' },
      { k: '적용 시설', v: '집진·여과 방지시설' },
      { k: '통신·연동', v: 'RS-485 · EVS-G200' },
      { k: '제조', v: 'Sensys 차압 트랜스미터' },
    ],
  },
  i3: {
    blurb: 'RTD 방식 온도센서로 배출가스·설비의 온도를 계측합니다. 유입구 등 핵심 지점에 설치해 공정 온도를 실시간 감시합니다.',
    features: [
      { icon: 'temp', title: 'RTD 방식 온도센서', desc: '배출가스·설비 온도 계측' },
      { icon: 'check', title: '유입구 설치', desc: '핵심 지점 실시간 감시' },
    ],
    // 범용 RTD 온도센서(써모웰+헤드형, 브랜드 무표기). 검증 사실만 — 상세 사양(범위·정확도)은 데이터시트 필요.
    specs: [
      { k: '측정 대상', v: '배출가스·설비 온도' },
      { k: '센서 방식', v: 'RTD 측온저항체 (써모웰+헤드형)' },
      { k: '설치', v: '유입구 등 핵심 지점' },
      { k: '게이트웨이 연동', v: 'EVS-G200 (온도센서 1CH)' },
    ],
  },
  i4: {
    blurb: '흡수·세정 시설의 수소이온농도(pH)와 ORP를 정밀 측정해 처리 효율과 약품 투입 상태를 감시합니다. 게이트웨이와 연동해 원격 관제됩니다.',
    features: [
      { icon: 'ph', title: 'pH / ORP 측정', desc: '수소이온농도·산화환원 전위' },
      { icon: 'check', title: '흡수·세정 시설', desc: '처리 효율·약품 상태 감시' },
    ],
    // 브랜드=Cheonsei(천세) MESTAR pH/ORP 컨트롤러(제품 이미지 확인). 제조사 공개 사양(cheonsei.co.kr) 반영.
    specs: [
      { k: '측정 항목', v: 'pH / ORP(mV)' },
      { k: '자동 교정', v: 'pH 4 · 7 · 9 자동 보정' },
      { k: '온도 보상', v: '액온 자동 보상' },
      { k: '통신', v: 'RS-485 (기록계·PLC 연동)' },
      { k: '적용 시설', v: '흡수·세정 방지시설' },
      { k: '제조', v: 'Cheonsei MESTAR' },
    ],
  },
};

export const SAFETY_TERMINALS: Instrument[] = [
  // [2026-09-06 사용자 확정 'B안'] 카드 이미지 = 중요부위(방사 아이콘 5개+기기+LCD)만 PIL 무손실 크롭한
  //   정사각 파일. 원본 wbgt-device.png(1388×554, 상/하 텍스트 포함)에서 x455~774·y143~460 영역만 잘라
  //   계측기 카드와 '동일한 정사각 박스'에 풀블리드(fill)로 꽉 채움. 원본 회색 배경 그대로 유지.
  //   철학: 이미지 박스는 크기 통일 + 이미지는 중요부위 포커싱(크롭). 상세=[[evs-image-box-design]] / ROADMAP-D.
  // [2026-09-09 사용자] 체감온도계는 /products에 상세 블록이 없음(#instruments=환경 계측기 전용) → 진짜 상세인 안전 도메인 제품섹션으로.
  { id: 't1', label: 'AI 온열질환 IoT 체감온도계', image: '/images/products/wbgt-cluster.png', fill: true, to: '/safety#products', desc: '실시간 체감온도(WBGT)로 폭염·온열질환 위험을 SaaS로 관제합니다.' },
  { id: 't2', label: '가스검침기', soon: true, desc: '밀폐공간 유해가스 실시간 감지 — SaaS 통합관제 편입 예정.' },
];

// ============================================================================
//  도메인 기본 페이지 데이터 (/safety · /environment — 2026-09-08 사용자, 3 기본 페이지 축)
//  원칙: ① 제품 우선(각 페이지 상단에 도메인 제품 라인업 = PRODUCTS_CATALOG 도메인 필터)
//        ② 서브메뉴는 페이지-당 앵커 섹션(topics[].id = 헤더 서브메뉴 to '/safety#id' 대상).
//        ③ 히어로 = 전 페이지 공통 이미지(PAGE_HERO_IMAGE). 문구는 기존 검증본 재사용(환각 금지).
//  SaaS·GreenLink 등은 '부차' 배치(제품 다음). cf. [[evs-page-ux-standards]] · DOMAIN_SUMMARY.
// ============================================================================
export interface DomainTopic {
  id: string;              // 앵커 id(서브메뉴 연결) — 예: 'saas','thermo','gateway','ef'
  badge?: string;          // 상태 뱃지(예: '운영중','예정','한국환경공단 승인·확인')
  // [2026-10-26 사용자] 제품 일반 소구의 '한국환경공단 인증'은 전사적으로 '승인·확인'으로 통일.
  //   근거: G200은 '인증'이 아닌 소규모배출시설 관리시스템 '승인·확인' 성격(evstech 대조). 기획서 v7 §8 지적.
  //   ★예외(유지): 주요연혁(HISTORY)·인증페이지(CERTS)의 'IoT 통신 프로토콜 3.0/복수/교대 인증'은
  //     evstech 주요연혁 실측과 동일한 공식 이벤트명이라 그대로 둠(실제 프로토콜 인증 이력). 상세: docs/EVS_v7_전수비교_2026-10-26.md
  title: string;
  body: string;
  bullets?: string[];
  trial?: boolean;         // true면 'SaaS 데모 체험하기' 버튼(TrialModal 오픈) 노출
}
export interface DomainPageData {
  key: ProductDomain;      // 'safety' | 'environment' — 제품 필터 + 강조색(구분자)
  heroImage?: string;      // [2026-09-08 사용자] 도메인 전용 히어로 이미지. 없으면 PAGE_HERO_IMAGE. (제품은 유지)
  slogan: string;          // 히어로 영문 eyebrow
  sloganKo: string;        // 히어로 국문 대제목
  sub: string;
  intro: string;
  productsTitle: string;
  productsDesc: string;
  topics: DomainTopic[];   // 앵커 섹션(제품 라인업 다음)
}

export const SAFETY_PAGE: DomainPageData = {
  key: 'safety',
  heroImage: '/images/hero/hero-safety.jpg', // [2026-09-08 사용자] 안전 전용 히어로 이미지
  // [2026-09-08 사용자] 히어로 = 메인(HERO)과 동일 2단(큰 슬로건 'IoT로 ~하다' + 작은 슬로건). 안전 도메인 문구.
  slogan: 'Safety', // (미사용 — 히어로에서 영문 eyebrow 제거, 메인과 동일하게 국문 2단만)
  sloganKo: 'IoT로 산업 현장의 안전을 지키다',
  sub: '온열질환·밀폐가스 위험을 실시간 감지하고 통합 관제 SaaS로 대응합니다.',
  intro: '근로자 재해 예방을 위한 EVS 안전 솔루션입니다. 현장 IoT 단말이 위험을 실시간 감지하고, 통합 관제 SaaS가 경보·기록·대응까지 자동화합니다.',
  productsTitle: '안전 제품',
  productsDesc: '체감온도계(WBGT)부터 가스검침기까지, 현장 안전 데이터를 실시간으로 측정하는 IoT 단말입니다.',
  topics: [
    { id: 'saas', badge: '운영중', title: '통합 관제 SaaS',
      body: '현장 IoT 단말이 수집한 데이터를 실시간으로 통합 관제합니다. 위험 단계 자동 판정, 앱·SMS 즉시 알림, 법정기록 자동 저장까지 한 화면에서 처리합니다.',
      trial: true },
    { id: 'thermo', badge: '체감온도계', title: '온열질환 — AI 체감온도계(WBGT)',
      body: '현장 체감온도(WBGT)를 실시간 산출해 폭염·온열질환 위험을 관제합니다. 4채널 앱·SMS 즉시 알림, 관심·주의·경고·위험 4단계(체감온도 31·33·35·38℃) 자동 대응, 법정기록 자동 저장으로 고용노동부 체감온도 33도 휴식 의무에 대응합니다.',
      bullets: ['체감온도(WBGT) 실시간 산출', '관심·주의·경고·위험 4단계 경보', '법정기록 자동 저장'] },
    { id: 'gas', badge: '예정', title: '밀폐가스 검침 (준비중)',
      body: '밀폐공간의 유해가스를 실시간 감지하는 IoT 단말을 준비하고 있습니다. 통합 관제 SaaS에 편입될 예정입니다.' },
  ],
};

export const ENVIRONMENT_PAGE: DomainPageData = {
  key: 'environment',
  heroImage: '/images/hero/hero-environment.jpg', // [2026-09-08 사용자] 환경 전용 히어로 이미지
  // [2026-09-08 사용자] 히어로 = 메인과 동일 2단. 환경 도메인 문구(메인 헤드라인과 같은 '모니터링' 결).
  slogan: 'Environment', // (미사용 — 영문 eyebrow 제거)
  sloganKo: 'IoT로 대기환경을 실시간 모니터링하다',
  sub: '소규모 대기배출을 측정해 환경부 GreenLink로 전송합니다.',
  intro: '대기배출 원격 모니터링을 위한 EVS 환경 솔루션입니다. 게이트웨이와 계측기가 방지시설 가동 데이터를 실시간 수집하고, 환경부 GreenLink로 자동 전송합니다.',
  productsTitle: '환경 제품',
  productsDesc: 'IoT Gateway(EVS-G200)부터 전류·차압·온도·pH 계측기까지, 방지시설 데이터를 정밀 측정합니다.',
  topics: [
    { id: 'gateway', badge: '한국환경공단 승인·확인', title: 'IoT Gateway 원격 모니터링',
      body: 'IoT 센서와 게이트웨이를 하나로 통합한 EVS-G200이 방지시설 가동 데이터를 실시간 수집·전송합니다. 유·무선(유선·WiFi·LTE) 통신과 디스플레이를 갖춰 현장 설치·운영이 간편합니다.',
      bullets: ['유·무선 통신(유선·WiFi·LTE)', '한국환경공단 승인·확인', '환경부 GreenLink 전송'] },
    // [2026-09-08] '제품 위주' 결정으로 규제성 섹션(방지시설 정의·법규) 제거 → 신규 '설치안내' 별도 페이지로 이관.
    //   여기(환경)엔 제품/서비스 성격(게이트웨이·GreenLink 관제)만 유지.
    { id: 'greenlink', title: '관제 — 환경부 GreenLink 전송',
      body: '측정 데이터를 환경부 GreenLink로 자동 전송해 법적 보고 의무에 대응합니다. 제조부터 설치·A/S까지 EVS가 직접 지원합니다.' },
  ],
};

// ============================================================================
//  설치안내 — 단일 앵커 페이지 (/guide · 2026-09-08 사용자 재설계)
//  "설치안내가 너무 요약됨 → 수직 확장·SVG 아이콘·쇼핑몰식 풍부하게. 하위메뉴=각 섹션 anchor."
//  구성 = evstech 4원본을 섹션으로: 제조업(/igm)·방지시설(/igi)·배출시설(/ef)·설치대상(/its).
//  디자인 = evs.com/applications 식(라이트 배경·중앙 헤딩+시안 강조·플랫 라인 SVG 아이콘·페이드업 리빌).
//  배경 white([[evs-page-background-scheme]]). SVG=GUIDE_ICON([[GuideIcons]]). 상세=[[evs-env-content-map]].
// ============================================================================
export interface GuideBlock {
  kind: 'lead' | 'features' | 'featlist' | 'rows' | 'table' | 'facts' | 'chips' | 'product' | 'stages' | 'lcd' | 'saas' | 'diagram';
  diagram?: 'env' | 'safety';    // kind:'diagram' 변형(기본 env). 안전=시안 테마·매뉴얼 기반 SaaS 흐름
  title?: string;
  body?: string;
  features?: { icon: string; title: string; desc: string }[];                                        // 아이콘 피처 그리드
  stages?: { level: string; temp: string; color: string; action: string }[];                          // 안전: 체감온도 4단계(계단형)
  // [2026-09-08 사용자] evs.com/applications 식 '좌우 교대 이미지+텍스트 행'. 실사=evstech 원본(/igi·/ef·/its).
  //   accent=행별 고유 강조색(제목·마커·아이콘틴트). 명확·세련·깔끔(코포레이트이벤트 방식).
  rows?: { image: string; icon?: string; accent?: string; title: string; subtitle?: string; desc: string; meta?: { k: string; v: string }[] }[];
  table?: { head: string[]; rows: string[][]; note?: string };
  facts?: { icon?: string; k: string; v: string }[];                                                  // 핵심 지표 카드
  chips?: { t: string }[];                                                                            // 짧은 태그(오염물질 등)
  product?: { image: string; name: string; badges?: string[]; paras: string[]; specs: { k: string; v: string }[] }; // 제품 상세(EVS-G200) — badges=blue/orange 강조
}
export interface GuideSection {
  id: string;                    // 앵커 id(서브메뉴 → /guide#id)
  eyebrow: string;               // 영문 소제목
  title: string;                 // 섹션 제목
  accent?: string;               // 제목 내 시안 강조 표기(부분 문자열)
  intro?: string;
  tone: 'light' | 'soft';        // 배경 교대(흰/연회색)
  blocks: GuideBlock[];
}
export interface InstallGuideData {
  key: ProductDomain;            // 'safety'(블루) | 'environment'(그린) — 탭·강조색 테마
  hero: { eyebrow: string; title: string; sub: string };
  sections: GuideSection[];
}

// 환경 설치안내(대기배출) — 기존 구조(제조업·방지시설·배출시설·설치대상)
export const INSTALL_GUIDE: InstallGuideData = {
  key: 'environment',
  hero: {
    eyebrow: 'INSTALL GUIDE',
    title: '소규모 대기배출 IoT 설치 안내',
    sub: 'IoT 게이트웨이 제조부터 방지시설·배출시설·설치대상 기준까지 한눈에.',
  },
  sections: [
    // ① IoT GW 제조업 (evstech /igm) — 제조사 소개 + 3응용분야 + 제품솔루션(EVS-G200)
    {
      id: 'manufacture', eyebrow: 'MANUFACTURER', title: 'IoT 게이트웨이 제조사, 이브이에스', accent: '이브이에스', tone: 'light',
      intro: '이브이에스는 환경 보호와 산업 발전을 동시에 고려하는 혁신적인 IoT 게이트웨이 제조사입니다. 자체 기술 개발과 고성능 제품으로 소규모 대기환경 사업장의 요구에 대응합니다.',
      blocks: [
        // [2026-09-09 사용자] 데이터사이트(/igm)의 3응용분야 그래픽을 다크 스타디움 순환 다이어그램(WHAT WE DO)으로 교체.
        //   시안 = Claude Design `target SVGs/EVS 사업영역 다이어그램 (단독형).html` → BizLoopDiagram 컴포넌트로 이식.
        { kind: 'diagram' },
        { kind: 'product', title: 'IoT GW 제품과 솔루션', product: {
          image: '/images/products/gateway-g200.png', name: 'IoT Gateway EVS-G200', // [2026-10-08] EVS-G200 실제 제품샷 교체(일관성)
          badges: ['한국환경공단 승인·확인', '전송률 우수 제조사'],
          paras: [
            '이브이에스가 제조한 IoT 게이트웨이는 프로토콜 V1.0·V2.6.4·V3.0, 복수형·교대형 시스템에 인증을 마쳤으며, 전송률 우수 제조사로 선정되었습니다.',
            '한국환경공단 승인·확인 제품과 전송률 우수 제조사 성과를 바탕으로, 소규모 배출시설관리시스템 구축 분야에서 안정적으로 사용할 수 있는 고성능 제품을 제공합니다.',
          ],
          specs: [
            { k: 'CPU', v: 'Intel Cherry Trail Quad Core 1.8GHz' },
            { k: 'Memory / Storage', v: '4G DDR3 / 32G' },
            { k: 'O/S', v: 'Windows 10 IoT' },
            { k: '광대역 통신', v: 'Ethernet · LTE(VPN)' },
            { k: '근거리 통신', v: 'RS-485 · RS-232' },
            { k: '센서', v: '4~20mA 10CH · 온도 1CH · 55CH 확장' },
            { k: 'Display', v: '7" LCD 터치스크린' },
            { k: '전원', v: 'AC 220V' },
          ],
        }},
        { kind: 'features', title: '이브이에스 제조 강점', features: [
          { icon: 'certified', title: '한국환경공단 승인·확인', desc: 'EVS-G200 단수형·복수형·교대형 전 모델 승인·확인 완료.' },
          { icon: 'signal', title: '전송률 우수 제조사', desc: '안정적인 데이터 전송 성과를 인정받은 제조사입니다.' },
          { icon: 'solution', title: '맞춤 솔루션', desc: '소규모 대기환경 사업장 사양에 맞춰 다양하게 제공합니다.' },
        ]},
      ],
    },
    // ② 방지시설의 정의 (evstech /igi) — 별표9의2 6종
    {
      id: 'prevention', eyebrow: 'PREVENTION FACILITIES', title: '대기오염을 걸러내는 방지시설 6종', accent: '6종', tone: 'soft',
      intro: '방지시설은 규모가 작은 공장·사업장의 대기오염 물질을 정화·제거하는 장치입니다. 아래 6가지가 IoT 측정기기 부착 대상이며, 시설별로 부착하는 계측기 종류가 다릅니다. (대기환경보전법 시행규칙 별표 9의2)',
      blocks: [
        // 6종 = 좌우 교대 이미지+텍스트 행(실사=/igi 원본). 행별 고유 강조색.
        { kind: 'rows', rows: [
          { image: '/images/guide/igi_100.jpg', icon: 'cyclone', accent: '#00AEEF', title: '원심력집진시설', subtitle: '곡물업 등',
            desc: '먼지 입자가 포함된 공기를 원형 챔버로 회전시켜, 원심력으로 무거운 입자를 벽으로 밀어내 분리·제거합니다.',
            meta: [{ k: '처리원리', v: '원심력(전처리용)' }, { k: '오염물질', v: '입자상' }, { k: 'IoT 측정', v: '(송풍)전류계' }] },
          { image: '/images/guide/igi_200.jpg', icon: 'filter', accent: '#35c17e', title: '여과집진시설', subtitle: '자동차수리업 등',
            desc: '섬유·직물 필터로 공기 중 먼지와 미세 입자를 걸러 제거합니다. 미세먼지까지 제거해 효율이 높습니다.',
            meta: [{ k: '처리원리', v: '여과포 필터(Filter bag)' }, { k: '오염물질', v: '입자상' }, { k: 'IoT 측정', v: '(방지)차압계·온도계 · (송풍)전류계' }] },
          { image: '/images/guide/igi_300.jpg', icon: 'adsorb', accent: '#6366f1', title: '흡착에 의한 시설', subtitle: '자동차수리업 등',
            desc: '활성탄·제올라이트 등 다공성 흡착제로 유해 가스·증기를 흡착해 제거합니다. 가스상 오염물질 처리에 적합합니다.',
            meta: [{ k: '처리원리', v: '활성탄 흡착' }, { k: '오염물질', v: '가스상' }, { k: 'IoT 측정', v: '(방지)차압계·온도계 · (송풍)전류계' }] },
          { image: '/images/guide/igi_400.jpg', icon: 'electro', accent: '#f59e0b', title: '전기집진시설', subtitle: '섬유제조 등',
            desc: '고전압으로 먼지 입자에 전하를 부여한 뒤, 반대 전하의 전극판에 부착시켜 공기에서 분리·제거합니다.',
            meta: [{ k: '처리원리', v: '직류 고전압' }, { k: '오염물질', v: '입자상' }, { k: 'IoT 측정', v: '(방지)전류계 · (송풍)전류계' }] },
          { image: '/images/guide/igi_500.jpg', icon: 'scrub', accent: '#8b5cf6', title: '세정집진시설', subtitle: '도금업 등',
            desc: '공기 중 입자를 물방울과 접촉시켜 제거하고, 유해가스도 함께 흡수해 환경오염을 줄입니다.',
            meta: [{ k: '처리원리', v: '스크러버 물분사' }, { k: '오염물질', v: '입자상' }, { k: 'IoT 측정', v: '(펌프)전류계 · (송풍)전류계' }] },
          { image: '/images/guide/igi_600.jpg', icon: 'droplet', accent: '#14b8a6', title: '흡수에 의한 시설', subtitle: '도금업 등',
            desc: '액체 흡수제로 가스 상태의 오염물질을 선택적으로 흡수해 대기 배출을 막습니다.',
            meta: [{ k: '처리원리', v: '반건식반응(중금속 흡수)' }, { k: '오염물질', v: '가스상' }, { k: 'IoT 측정', v: '(방지)pH계 · (펌프)전류계 · (송풍)전류계' }] },
        ]},
      ],
    },
    // ③ 배출시설 (evstech /ef)
    {
      id: 'emission', eyebrow: 'EMISSION FACILITIES', title: '대기오염물질을 내보내는 배출시설', tone: 'light',
      intro: '배출시설은 공장·발전소·제조업체의 장비·기계처럼 연소·화학반응·물리적 변화를 통해 대기 중에 오염물질을 방출하는 시설입니다. 인체·환경에 악영향을 미쳐 법적으로 규제됩니다.',
      blocks: [
        { kind: 'rows', rows: [
          { image: '/images/guide/ef_10.jpg', icon: 'factory', accent: '#00AEEF', title: '산업활동이 대기로 내보내는 오염물질', subtitle: '배출시설이란?',
            desc: '연소·화학반응·물리적 변화를 통해 대기 중에 오염물질을 방출하는 시설입니다. 인체·환경에 악영향을 미쳐 법적으로 규제되며, 규모·배출량에 따라 1~5종 사업장으로 나뉩니다.' },
        ]},
        { kind: 'chips', title: '대표 대기오염물질', chips: [
          { t: '미세먼지' }, { t: '질소산화물(NOx)' }, { t: '황산화물(SOx)' }, { t: '휘발성 유기화합물(VOCs)' }, { t: '일산화탄소(CO)' },
        ]},
        { kind: 'table', title: '대기 배출시설의 종류 (1종~5종)',
          table: { head: ['종별', '대기오염물질 발생량(연간)'], rows: [
            ['1종 사업장', '80톤 이상'],
            ['2종 사업장', '20톤 이상 ~ 80톤 미만'],
            ['3종 사업장', '10톤 이상 ~ 20톤 미만'],
            ['4종 사업장', '2톤 이상 ~ 10톤 미만'],
            ['5종 사업장', '2톤 미만'],
          ], note: 'IoT 측정기기 부착 대상은 소규모 사업장인 4종·5종입니다.' } },
        { kind: 'lead', body: '전기만 사용하는 간접가열시설, 옥내 자연건조 시설, 붓·롤러만 사용하는 도장시설, 이동식 시설 등 일부는 배출시설에서 제외됩니다.' },
      ],
    },
    // ④ 설치대상 기준 (evstech /its)
    {
      id: 'target', eyebrow: 'INSTALLATION TARGET', title: 'IoT 측정기기, 누가 언제 설치하나', tone: 'soft',
      intro: '대기환경보전법 제17조에 따라 소규모 대기배출사업장(4·5종)은 사물인터넷(IoT) 측정기기 부착 의무 대상입니다. 2022년 개정으로 실시간 모니터링이 필요한 사업장까지 규제가 확대되었습니다.',
      blocks: [
        { kind: 'facts', facts: [
          { icon: 'building', k: '대상', v: '소규모 4·5종 사업장' },
          { icon: 'doc', k: '의무 근거', v: '대기환경보전법 제17조' },
          { icon: 'calendar', k: '설치 기한', v: '2025. 6. 30까지' },
          { icon: 'won', k: '설치비 지원', v: '환경부 약 90%' },
        ]},
        { kind: 'rows', rows: [
          { image: '/images/guide/its_10.jpg', icon: 'signal', accent: '#35c17e', title: '왜 IoT 측정기기인가', subtitle: '설치 목적',
            desc: '방지시설의 가동 상태와 배출가스 농도를 실시간으로 확인해, 현장 방문 없이 원격으로 배출 상태를 관리합니다. 소규모 사업장도 실시간 데이터 관리로 대기오염을 줄이고 법적 규제를 준수할 수 있으며, 환경부는 설치비의 약 90%를 지원합니다.' },
        ]},
        { kind: 'table', title: '방지시설별 부착 측정기기 (시행규칙 별표 9의2)',
          table: { head: ['방지시설', '배출시설', '방지시설'], rows: [
            ['원심력집진시설', '전류계', '전류계'],
            ['세정집진시설', '전류계', '전류계'],
            ['여과집진시설', '전류계', '전류계 · 차압계 · 온도계'],
            ['전기집진시설', '전류계', '전류계'],
            ['흡수에 의한 시설', '전류계', '전류계 · pH계'],
            ['흡착에 의한 시설', '전류계', '전류계 · 차압계 · 온도계'],
          ] } },
        { kind: 'features', title: '주요 대상 업종', features: [
          { icon: 'factory', title: '도장업', desc: '페인트 도장 공정의 휘발성 유기화합물(VOCs) 등' },
          { icon: 'doc', title: '인쇄업', desc: '잉크·용제 사용 인쇄 과정의 오염물질' },
          { icon: 'solution', title: '금속 가공업', desc: '용접·절단·가공 공정의 미세먼지 등' },
          { icon: 'droplet', title: '섬유·염색업', desc: '염료·화학약품 사용 공정의 오염물질' },
          { icon: 'building', title: '제조업', desc: '화학물질 사용·연소 공정의 소규모 제조업체' },
        ]},
      ],
    },
  ],
};

// 안전 설치안내(온열질환) — [2026-09-08 사용자] 환경과 '구조를 다르게' 융합 작성. 블루 계열.
//   원본=안전 뉴스 실측(target downloads/안전: legal-rest-standard 등 + newsData). 구조: 법규 → 체감온도 4단계 → 사업주 의무 → 체감온도계 제품 → 가스검침(예정).
export const SAFETY_GUIDE: InstallGuideData = {
  key: 'safety',
  hero: {
    eyebrow: 'SAFETY INSTALL GUIDE',
    title: '온열질환 예방 IoT 설치 안내',
    sub: '체감온도 법적 기준부터 AI 체감온도계 설치까지.',
  },
  sections: [
    // ⓪ 소개 + HOW IT WORKS 다이어그램 — 환경 manufacture 섹션과 대칭으로 최상단 배치(2026-09-09 사용자).
    //   EVS = 온열질환 예방 AI 체감온도계(WBGT) 제조사. 다이어그램은 매뉴얼 기반 SaaS 흐름(안전=앰버·레드).
    {
      id: 'about', eyebrow: 'SMART SAFETY', title: 'AI 체감온도계 제조사, 이브이에스', accent: '이브이에스', tone: 'light',
      intro: '이브이에스는 산업 현장의 온열질환을 예방하는 AI 체감온도계(WBGT) 제조사입니다. 실시간 측정·경보·기록을 하나로 묶어, 사업주의 법적 의무 이행과 근로자 안전을 동시에 지원합니다.',
      blocks: [
        { kind: 'diagram', diagram: 'safety' },
      ],
    },
    // ① 법규(왜 필요한가)
    {
      id: 'law', eyebrow: 'REGULATION', title: '폭염, 이제 사업주의 법적 의무입니다', accent: '법적 의무', tone: 'light',
      // 근거=폭염 대응지침 PDF(p2,3): 산업안전보건법 개정 '24.10.22, 산업안전보건기준 규칙 '25.7.17 시행.
      intro: '2024년 10월 산업안전보건법 개정으로 폭염이 건강위험 요인으로 명확해졌고, 사업주 보건조치가 의무화됐습니다. 구체적 조치는 「산업안전보건기준에 관한 규칙」으로 2025년 7월 17일 공포·시행됐습니다.',
      blocks: [
        { kind: 'features', features: [
          { icon: 'doc', title: '산업안전보건법 개정', desc: '2024.10 폭염을 건강위험 요인으로 명확화, 사업주 보건조치를 의무화했습니다.' },
          { icon: 'alert', title: '규칙 시행 (2025.7.17)', desc: '온열질환 예방 구체적 조치를 산업안전보건기준 규칙으로 규정·시행합니다.' },
          { icon: 'monitor', title: '예방체계 마련', desc: '현장 여건에 맞는 온열질환 예방체계를 사업주가 마련해야 합니다.' },
        ]},
      ],
    },
    // ② 체감온도 4단계(계단형 — 안전 전용 시각화)
    {
      id: 'stages', eyebrow: 'HEAT INDEX', title: '체감온도 단계별 기준', tone: 'soft',
      // [2026-09-08 사용자] 색 카드 대신 '글자(텍스트 표)'로. 근거=폭염 대응지침 PDF + 산업안전보건기준 규칙.
      intro: '체감온도는 기온·습도·바람으로 사람이 느끼는 온도로, 단순 기온보다 3~5℃ 높게 나타납니다. 산업안전보건기준에 관한 규칙(2025.7.17 시행)은 체감온도 31℃ 이상 장시간 작업을 「폭염작업」으로 정의하고 단계별 조치를 규정합니다.',
      blocks: [
        { kind: 'table', title: '체감온도 단계별 기준 · 조치 (기상청 · 고용노동부)',
          table: { head: ['단계', '체감온도', '기준 · 조치'], rows: [
            ['관심', '31℃ 이상', '「폭염작업」 시작 기준 — 건강상태 수시 확인 · 수분 섭취'],
            ['주의', '33℃ 이상', '폭염주의보 기준 — 2시간마다 20분 이상 휴식 (안전보건규칙 제560조)'],
            ['경고', '35℃ 이상', '폭염경보 기준 — 무더위시간(14~17시) 옥외작업 단축·조정'],
            ['위험', '38℃ 이상', '옥외작업 중지 권고'],
          ], note: '체감온도는 작업장소 바닥 면으로부터 약 1.2~1.5m 높이에서 측정 (별표 13의2).' } },
        { kind: 'lcd', title: '현장에서 실시간으로 확인',
          body: '설치된 체감온도계 LCD가 현재 체감온도(WBGT)와 폭염단계를 실시간으로 표시하고, 단계별 필요조치를 화면에 안내합니다. 관리자는 통합 관제 SaaS로 원격에서도 동일하게 확인합니다.' },
      ],
    },
    // ②-2 SaaS·앱 (사업장 총괄·공정 담당자용) — [2026-09-08 사용자] KPI·그래프(최고관리자용) 제거, SVG 아이콘 피처만.
    //   근거=사용자 매뉴얼(대시보드 캡처 미사용). 조치 입력=산안법 감사·관공서 제출 근거(매뉴얼 6.경보/조치). cf. [[evs-dashboard-saas-for-homepage]].
    {
      id: 'saas', eyebrow: 'SaaS & APP', title: 'SaaS·앱으로 현장을 손안에', accent: '손안에', tone: 'light',
      intro: '현장 체감온도계 데이터가 클라우드로 전송돼, 사업장 총괄·공정 담당자가 웹과 모바일 앱으로 체감온도·경보를 실시간 확인하고 조치까지 기록합니다.',
      blocks: [
        // [2026-09-08→09 사용자] 12개(3×4 짝) 컴팩트 목록. 텍스트 위주·풍성·심플. 아이콘=facts 스타일(52px·무배경).
        //   [09-09] 다중 사업장 통합 관제 + 데이터 장기 보관(시간단위 5년, 매뉴얼 purgeOldHourlyData(5) 실측) 추가 → 홀수 빈칸 해소.
        //   (HOW IT WORKS 다이어그램은 2026-09-09 최상단 'about' 섹션으로 이동 — 환경 manufacture와 대칭)
        //   ★알림은 크게 2종(사용자 확정 + 매뉴얼 근거): ①단계 경보(31/33/35/38 진입 시 채널 발송)
        //     ②휴게 안내(33℃↑ 폭염작업 시 2시간마다 20분 휴식 주기 = 산안법 제560조. 매뉴얼 '경보/조치 이력' p17: 휴게 시작·종료 알림).
        //   + 채널별 ON/OFF · 근무/휴게/휴일 설정(사업장별 편집) · AI 분석 추가. 근거: [[evs-dashboard-saas-for-homepage]] · [[evs-safety-heat-regulation]]
        { kind: 'featlist', features: [
          { icon: 'bell', title: '단계 경보 알림', desc: '체감온도 관심31·주의33·경고35·위험38 진입 시 SMS·앱푸시(FCM)·이메일·카카오톡으로 담당자에게 즉시 경보합니다.' },
          { icon: 'cycle', title: '휴게 안내 알림', desc: '체감온도 33℃↑ 폭염작업 시 2시간마다 20분 휴식 주기를 자동 안내 — 휴게 시작·종료를 알려 법정 휴식을 지킵니다.' },
          { icon: 'toggle', title: '채널별 ON/OFF', desc: 'SMS·이메일·앱푸시(FCM)·카카오톡 알림을 채널별로 개별 ON/OFF — 담당자마다 원하는 경로로만 경보를 받습니다.' },
          { icon: 'calendar', title: '근무·휴게·휴일 설정', desc: '사업장별 근무시간·휴게시간·휴일을 직접 수정해 현장 운영 스케줄에 맞춰 알림을 보냅니다.' },
          { icon: 'monitor', title: '현장 그래프 확인', desc: '체감온도(WBGT) 추이를 관심·주의·경고·위험 4단계 기준선과 함께 실시간으로 봅니다.' },
          { icon: 'building', title: '다중 사업장 통합 관제', desc: '여러 사업장을 한 계정으로 통합 관제하고, 사업장별 평균·최고·최저를 한눈에 비교합니다.' },
          { icon: 'ai', title: 'AI 분석·예측', desc: '기상 데이터와 현장 센서 추이를 AI가 분석해 위험 상승을 예측·요약합니다.' },
          { icon: 'action', title: '법정 조치 입력·기록', desc: '등급별 조치를 법정 가이드 프리셋으로 기록 — 관공서 제출·산업안전보건법 감사의 근거가 됩니다.' },
          { icon: 'doc', title: '월간 기록부', desc: '체감온도·조치 내역을 월 단위 기록부로 자동 생성해 법적 보고에 대응합니다.' },
          { icon: 'database', title: '데이터 장기 보관', desc: '현장 센서 데이터를 시간단위로 최대 5년간 보관 — 장기 추이 분석과 산업안전보건법 감사 대응의 근거가 됩니다.' },
          { icon: 'certified', title: '점검표·응급조치', desc: '온열질환 예방 점검표와 119·응급조치 안내로 현장 대응을 표준화합니다.' },
          { icon: 'phone', title: '모바일 앱', desc: '사업장 총괄·공정 담당자가 Android 앱으로 언제 어디서나 확인합니다.' },
        ]},
      ],
    },
    // ③ 사업주 법적 의무
    {
      id: 'duty', eyebrow: 'EMPLOYER DUTY', title: '사업주 법적 의무', tone: 'light',
      // 근거=산업안전보건기준 규칙 제559조(폭염작업=체감온도31℃↑)·제560조(33℃↑ 2시간마다 20분휴식)·별표13의2(측정1.2~1.5m).
      intro: '체감온도 33℃ 이상 작업장소에서 폭염작업을 하는 경우 2시간마다 20분 이상 휴식이 법적 의무입니다. 측정부터 기록까지 자동화된 체계가 필요합니다.',
      blocks: [
        { kind: 'facts', facts: [
          { icon: 'gauge', k: '폭염작업 기준', v: '체감온도 31℃ 이상 장시간 작업' },
          { icon: 'alert', k: '휴식 의무', v: '33℃↑ 작업 시 2시간마다 20분↑' },
          { icon: 'doc', k: '측정 기준', v: '바닥 1.2~1.5m 높이 체감온도' },
          { icon: 'certified', k: '근거 규정', v: '안전보건규칙 제559·560조' },
        ]},
      ],
    },
    // ④ AI 체감온도계 제품(강조 패널) — measure→analyze→alert→respond→record 5단계 자동화
    {
      id: 'wbgt', eyebrow: 'PRODUCT', title: 'AI 온열질환 IoT 체감온도계', tone: 'soft',
      intro: '현장 체감온도(WBGT)를 실시간 산출해 위험을 자동 경보하고 법정기록을 저장하는 IoT 단말입니다.',
      blocks: [
        { kind: 'product', title: '측정 → 분석 → 경보 → 대응 → 기록, 5단계 자동화', product: {
          // [2026-09-11 사용자] wbgt-device 원본에서 기기 전체+아이콘 클린 재크롭(437×358, 상단 'Step01'·하단 문구 제거).
          //   하단 문구('초정밀 ±0.5℃·1분 주기')는 이미지에서 빼고 아래 paras/specs로 이관(사용자 지시).
          image: '/images/products/wbgt-crop-v2.png', name: 'AI 온열질환 IoT 체감온도계',
          badges: ['고용노동부 기준 대응', 'SaaS 통합관제'],
          paras: [
            '기상청 공식 산출식으로 현장 체감온도(WBGT)를 실시간 계산해, 관심·주의·경고·위험 4단계로 위험도를 자동 판정합니다.',
            '초정밀 센서 기술로 ±0.5℃ 오차 이내로 측정하고 1분 주기로 클라우드에 전송하며, 4채널 앱·SMS 즉시 알림과 법정기록 자동 저장으로 측정→분석→경보→대응→기록의 5단계 자동화를 제공합니다.',
          ],
          // [2026-09-09 사용자] 환경(EVS-G200) 하드웨어 스펙과 대칭 → 안전(체감온도계)에도 LCD 하드웨어 스펙 추가.
          //   디스플레이/제어부/동작환경/전원 = LCD 데이터시트 실측(LCD SPEC/DMG48270C043_05WN.pdf: DWIN 4.3" TFT).
          //   측정·통신·기록 = 기존 검증본. 근거: [[evs-safety-wbgt-product]] · [[evs-dashboard-saas-for-homepage]].
          specs: [
            { k: '측정 항목', v: '체감온도(WBGT) · 기온 · 습도 실시간 산출' },
            { k: '센서 정확도', v: '±0.5℃ 이내 (초정밀 센서)' },
            { k: '전송 주기', v: '1분 주기 클라우드 전송' },
            { k: '위험 경보', v: '관심·주의·경고·위험 4단계 자동 판정' },
            { k: '알림', v: 'SMS · 앱푸시(FCM) · 이메일 · 카카오톡' },
            { k: '디스플레이', v: '4.3" TFT LCD · 480×272 · 262K색' },
            { k: '제어부', v: 'DWIN T5L0 ASIC · DGUS II · 8MB Flash' },
            { k: '밝기', v: '300nit LED 백라이트 (수명 20,000h↑)' },
            { k: '통신', v: 'WiFi · LTE · Ethernet (연결성 98%)' },
            { k: '동작 환경', v: '-20 ~ 70℃ · 습도 10~90%RH' },
            { k: '전원', v: 'DC 5V (정격 5W 이하)' },
            { k: '기록·관제', v: '법정기록 자동 저장 · SaaS 통합관제' },
          ],
        }},
      ],
    },
    // ⑤ 가스검침기(예정)
    {
      id: 'gas', eyebrow: 'COMING SOON', title: '밀폐공간 가스검침기', tone: 'light',
      intro: '밀폐공간의 유해가스를 실시간 감지하는 IoT 단말을 준비하고 있습니다. 통합 관제 SaaS에 편입될 예정입니다.',
      blocks: [
        { kind: 'lead', body: '산소결핍·유해가스 중독 위험이 있는 밀폐공간 작업의 안전을 위해, 가스 실시간 감지·경보 단말을 개발 중입니다.' },
      ],
    },
  ],
};

// 도메인별 설치안내 맵(탭 전환) — 안전(블루)/환경(그린)
export const GUIDES: Record<ProductDomain, InstallGuideData> = { environment: INSTALL_GUIDE, safety: SAFETY_GUIDE };

// 뉴스 & 소식 — EVS 공식 네이버 블로그(blog.naver.com/evstech) 실제 글 (2026-09-05 반영).
//   주제 = 온열질환·폭염·체감온도 IoT(= 안전 도메인 SaaS). naver 차단으로 사용자가 썸네일 직접 제공 →
//   public/images/news/ 에 무손실 저장(캡처 흰 여백 없는 풀블리드 원본). 제목은 블로그 원제를 가공(간결화).
//   날짜 = 한국식 KST "YYYY. MM. DD". 카테고리 = 블로그/설치 사례/제품소개.
export const NEWS: NewsItem[] = [
  { id: 'n1', category: '블로그', title: '폭염에 쓰러진 작업자, 사업주는 왜 처벌받았을까?', date: '2026. 09. 04', image: '/images/news/news-01.png' },
  { id: 'n2', category: '설치 사례', title: '대기업도 선택한 안전관리 솔루션 — AI 온열질환 IoT 체감온도계 설치 후기', date: '2026. 08. 24', image: '/images/news/news-02.png' },
  { id: 'n3', category: '블로그', title: '[긴급] "이 볼펜 기록, 인정 안 됩니다" — 2026 노동부 불시점검 대비법', date: '2026. 08. 18', image: '/images/news/news-03.png' },
  { id: 'n4', category: '설치 사례', title: '잠실 대형 건설현장 폭염 대비 — AI 온열질환 IoT 체감온도계 도입 현장', date: '2026. 08. 03', image: '/images/news/news-04.png' },
  { id: 'n5', category: '블로그', title: '2026년 폭염 대비 노동자 건강보호 대책 총정리', date: '2026. 07. 24', image: '/images/news/news-05.png' },
  { id: 'n6', category: '블로그', title: '[고용노동부 지침] 체감온도 33도 휴식 의무화 — 법적 리스크 ZERO 만드는 법', date: '2026. 07. 22', image: '/images/news/news-06.png' },
  { id: 'n7', category: '블로그', title: '현장 온열질환 사고, 수기 작성으로는 못 막습니다', date: '2026. 07. 20', image: '/images/news/news-07.png' },
  { id: 'n8', category: '블로그', title: '2026 고용노동부 폭염 집중점검 — 건설·산업현장 예방수칙과 IoT 대비법', date: '2026. 07. 10', image: '/images/news/news-08.png' },
  { id: 'n9', category: '제품소개', title: '[제품소개] 산업 현장 온열질환 완벽 차단 — EVS IoT 체감온도계 핵심 기능', date: '2026. 07. 06', image: '/images/news/news-09.png' },
];

// 사업 소개(Our solutions)
export const SOLUTIONS: Solution[] = [
  // 실시간 체감온도(WBGT) 모니터링 시스템 — 신규, 맨 첫번째(사용자 지시 2026-07-31).
  //   image = 실제 LCD UI 재현 SVG로 카드를 꽉 채움(사용자 지시).
  { id: 's5', title: '실시간 체감온도 모니터링', desc: '현장 WBGT·폭염단계 실시간 관제로 온열질환 예방', image: wbgtLcdSvg },
  { id: 's1', title: 'IoT GW 제조업', desc: 'IoT 게이트웨이·측정기기 제조' },
  { id: 's2', title: 'IoT GW 설치사업', desc: '대기오염 방지시설 IoT 모니터링 설치' },
  { id: 's3', title: '실시간 대기환경 모니터링', desc: '대기오염 실시간 측정·관제 플랫폼' },
  { id: 's4', title: '유지보수 · A/S', desc: '설치부터 운영·사후관리까지' },
];

// 제품 소개(커버플로우)
export const PRODUCTS: Product[] = [
  // 실시간 체감온도(WBGT) 모니터링 — 신규 제품(사용자 지시 2026-07-31), 제품소개 맨 앞.
  //   현장 LCD 표시기(EVS LCD 480×272) + 관제 대시보드. svg = prod-wbgt(실제 LCD 화면 재현).
  // description 1줄 유지(다른 제품과 줄 수·세로 위치 일치 위함 — 슬라이드가 중앙정렬이라 줄 수 다르면 위치 어긋남).
  { id: 'p0', name: '실시간 체감온도 모니터링', subtitle: 'IoT 체감온도(WBGT) 실시간 관제', description: '체감온도(WBGT)·폭염단계를 실시간 관제하는 현장 IoT 시스템', svg: wbgtSvg },
  { id: 'p1', name: 'EVS-G200', subtitle: 'IoT 센서·게이트웨이 일체형', description: 'IoT 센서와 게이트웨이를 하나로 통합한 대기환경 실시간 측정 장치', svg: gatewaySvg },
  { id: 'p2', name: '차압계', subtitle: '차압 측정 트랜스미터', description: '시설 내·외부의 압력 차를 정밀하게 측정하는 차압 트랜스미터', svg: dpSvg },
  { id: 'p3', name: '온도계', subtitle: '온도 측정 센서', description: '산업 현장의 온도를 실시간으로 정밀 측정하는 IoT 온도 센서', svg: tempSvg },
  { id: 'p4', name: 'pH계', subtitle: 'pH/ORP 측정 컨트롤러', description: '수질·용액의 pH와 ORP를 정밀 측정·제어하는 컨트롤러', svg: phSvg },
  { id: 'p5', name: '전류계', subtitle: '전류 측정 센서', description: '설비의 전류를 실시간으로 모니터링하는 IoT 전류 센서', svg: currentSvg },
];

// 고객 지원
export const SERVICES = {
  sectionTitle: '고객 지원',
  items: [
    { id: 'sv1', label: '기술 지원', sub: '전문 기술 상담과 지원', cta: '고객지원 문의',
      desc: '전화·이메일로 언제든 문의하실 수 있으며, 24시간 기술 지원팀이 제품 설치·운영 중 발생하는 문제를 신속하게 해결해 드립니다.' },
    { id: 'sv2', label: '사후관리 (A/S)', sub: '설치부터 운영까지 함께', cta: '자세히 보기',
      desc: '제조부터 설치, A/S까지 완벽한 사후관리로 안정적인 운영과 장비 수명을 보장합니다.' },
    { id: 'sv3', label: '자료실', sub: '제품 자료·매뉴얼 검색', cta: '자료실 바로가기',
      desc: '설치 가이드, 사용 설명서, 제품 사양서 등 제품 관련 자료를 자료실에서 확인하실 수 있습니다.' },
    { id: 'sv4', label: '견적 문의', sub: '도입 상담 및 견적 안내', cta: '자세히 보기',
      desc: '도입을 원하시면 견적 문의를 남겨 주세요. 현장 요구사항에 맞춘 최적의 IoT 솔루션 구성과 견적을 안내해 드립니다.' },
  ],
};

export const TRUST = {
  title: '인증 · 협력 기관',
  tagline: '정확한 측정과 신뢰가 핵심입니다. 환경부·한국환경공단 등 공인 기관의 인증을 바탕으로 안정적인 IoT 대기환경 솔루션을 제공합니다.',
  // evstech.co.kr(융합 대상 실사이트, http://www.evstech.co.kr/onbr/Brd/home/sky230)에서 수집한 실제 협력·인증 기관 로고
  //   원본: oncode.net CMS의 main_logo_10~60.png → public/images/partners/ 로 다운로드(2026-07-31).
  partners: [
    { name: '서울녹색환경지원센터', src: '/images/partners/partner-sgec.png', link: 'https://sgec.uos.ac.kr/' },
    { name: '충남녹색환경지원센터', src: '/images/partners/partner-cngec.png', link: 'http://cngec.or.kr/' },
    { name: '경기환경에너지진흥원', src: '/images/partners/partner-ggeea.png', link: 'https://www.ggeea.or.kr/' },
    { name: '환경부', src: '/images/partners/partner-me.png', link: 'https://me.go.kr/' },
    { name: '한국환경공단', src: '/images/partners/partner-keco.png', link: 'https://www.keco.or.kr/' },
    { name: '한국환경공단 그린링크', src: '/images/partners/partner-keco-greenlink.png', link: 'https://www.keco.or.kr/' },
  ],
};

// 주요 거래처 — evstech.co.kr(융합 대상) 설치사례 페이지의 '주요거래처' 32개 로고.
//   원본: oncode.net CMS의 img_installation_logo_10~320.png → public/images/clients/ 로 다운로드(2026-07-31).
//   name = 화면(사용자 제공 이미지) 좌→우·상→하 순서 기준 표기(로고 alt용).
//   ★디자인 결정(2026-07-31): 바로 위 Trust(인증·협력 기관)와 카드/헤더 언어는 공유하되,
//     32개로 많으므로 "다중행 연속 마퀴 + 회색 배경"으로 차별화(Trust=단일행 스텝 캐러셀).
export const CLIENTS = {
  title: '주요 거래처',
  // [2026-11-02 사용자지시] 과장 교정. 기존 문구의 '반도체·디스플레이'(v7·리뉴얼 실제 업종에 없음)·
  //   '국내외 기업'(리뉴얼 §4: 실매출 2건·초기단계로 '국내외' 근거 없음)·'신뢰합니다'(고객 보증 암시) 3중 과장을 제거.
  //   사실 서술형으로 하향. ★로고 32개는 evstech.co.kr(과거 회사사이트) CMS 공개자료(위 주석 L878)라 유지하되,
  //   리뉴얼 '거래처명 전부 비공개' 방침과의 상충은 사용자 확인 대기(2026-11-02). 연관: [[evs-keco-approval-wording]]
  tagline: '대기배출·산업 현장의 다양한 기업과 함께 제조·설치·A/S를 수행해 왔습니다.',
  items: [
    { name: 'FNS TECH', src: '/images/clients/client-01.png' },
    { name: '애경', src: '/images/clients/client-02.png' },
    { name: 'CM TECH', src: '/images/clients/client-03.png' },
    { name: 'CMDL', src: '/images/clients/client-04.png' },
    { name: 'Senic', src: '/images/clients/client-05.png' },
    { name: 'SINJIN', src: '/images/clients/client-06.png' },
    { name: '삼보판지', src: '/images/clients/client-07.png' },
    { name: '벽산', src: '/images/clients/client-08.png' },
    { name: 'Linde', src: '/images/clients/client-09.png' },
    { name: 'Lithium Plus', src: '/images/clients/client-10.png' },
    { name: 'DMI', src: '/images/clients/client-11.png' },
    { name: 'DBRAIN', src: '/images/clients/client-12.png' },
    { name: 'DREAMTECH', src: '/images/clients/client-13.png' },
    { name: '동희산기', src: '/images/clients/client-14.png' },
    { name: '대한폴리텍', src: '/images/clients/client-15.png' },
    { name: '대신메탈라이징', src: '/images/clients/client-16.png' },
    { name: '대동(daedong)', src: '/images/clients/client-17.png' },
    { name: 'DAIKIN', src: '/images/clients/client-18.png' },
    { name: 'DASAN', src: '/images/clients/client-19.png' },
    { name: 'NEOTEK', src: '/images/clients/client-20.png' },
    { name: 'GEUM SUNG', src: '/images/clients/client-21.png' },
    { name: 'PROTEC', src: '/images/clients/client-22.png' },
    { name: 'hy', src: '/images/clients/client-23.png' },
    { name: '천안도시공사', src: '/images/clients/client-24.png' },
    { name: '농심미분', src: '/images/clients/client-25.png' },
    { name: 'HYUNDAI MOBIS', src: '/images/clients/client-26.png' },
    { name: 'HANA Materials', src: '/images/clients/client-27.png' },
    { name: 'ENF', src: '/images/clients/client-28.png' },
    { name: 'TRANIX', src: '/images/clients/client-29.png' },
    { name: 'Jooyon', src: '/images/clients/client-30.png' },
    { name: 'EHWA', src: '/images/clients/client-31.png' },
    { name: 'enchem', src: '/images/clients/client-32.png' },
  ],
};

// 설치 사례 — evstech.co.kr(융합 대상) 설치사례 페이지 현장 사진 11장(img_installation_example_10~110.jpg,
//   596×596, 캡션 없음) → public/images/cases/case-01~11.jpg 다운로드(2026-07-31).
//   ※시험 도입(사용자 "일단 한번 넣어보자"). 사진 톤이 낡은 편 → 유지 시 교체/보정 검토.
export const CASES = {
  title: '설치 사례',
  tagline: '전국 산업 현장의 안전(체감온도계)·환경(대기배출 IoT) 설치·운영 사례입니다.',
  // [2026-09-09 사용자] 홈 설치사례 캐러셀에 안전(체감온도계) 커버를 맨 앞에 추가 → 안전이 디폴트로 먼저 보이게.
  //   (캐러셀은 items[0]부터 좌측 노출. 환경 현장사진 11장은 그대로 뒤에 이어짐.)
  items: [
    { id: 'safety-bigcorp', src: '/images/cases/bigcorp-install-review/cover.jpg' },
    { id: 'safety-jamsil', src: '/images/cases/jamsil-site/cover.jpg' },
    { id: 'ca1', src: '/images/cases/case-01.jpg' },
    { id: 'ca2', src: '/images/cases/case-02.jpg' },
    { id: 'ca3', src: '/images/cases/case-03.jpg' },
    { id: 'ca4', src: '/images/cases/case-04.jpg' },
    { id: 'ca5', src: '/images/cases/case-05.jpg' },
    { id: 'ca6', src: '/images/cases/case-06.jpg' },
    { id: 'ca7', src: '/images/cases/case-07.jpg' },
    { id: 'ca8', src: '/images/cases/case-08.jpg' },
    { id: 'ca9', src: '/images/cases/case-09.jpg' },
    { id: 'ca10', src: '/images/cases/case-10.jpg' },
    { id: 'ca11', src: '/images/cases/case-11.jpg' },
  ],
};

// ── 설치 사례(도입사례) — /cases 신규 페이지(2026-09-09 사용자). 설치안내와 다른 개념 → 헤더 신규 메뉴. ──
//   원본=네이버 블로그(evstech) 설치·AS 사례. 어제 추출본(text.txt/originals) → public/images/cases/{slug}/(JPG q90).
//   안전=체감온도계 설치(2건), 환경=대기배출 IoT 설치(9)·AS(5). 뉴스처럼 안전/환경 탭. 상세=evs.com/applications식(Phase B).
export type CaseCategory = '설치' | 'AS';
export interface CasePost {
  slug: string; domain: ProductDomain; category: CaseCategory;
  title: string; date: string; cover: string; summary: string;
}
export const CASE_POSTS: CasePost[] = [
  // 안전 — AI 온열질환 IoT 체감온도계 설치
  { slug: 'bigcorp-install-review', domain: 'safety', category: '설치', date: '2026. 08. 24',
    title: '대기업도 선택한 안전관리 — 체감온도계 설치 후기', cover: '/images/cases/bigcorp-install-review/cover.jpg',
    summary: '대기업 현장에 AI 온열질환 IoT 체감온도계를 설치해 실시간 폭염·온열질환을 관제한 도입 사례입니다.' },
  { slug: 'jamsil-site', domain: 'safety', category: '설치', date: '2026. 08. 03',
    title: '잠실 대형 건설현장 — 체감온도계 도입 현장', cover: '/images/cases/jamsil-site/cover.jpg',
    summary: '잠실 대형 건설현장의 폭염 대비를 위해 체감온도계(WBGT)를 도입한 설치 현장 사례입니다.' },
  // 환경 — 대기배출 IoT 측정기기 설치
  { slug: 'env-filter-adsorb2', domain: 'environment', category: '설치', date: '2025. 05. 07',
    title: '여과·흡착시설 2대 사업장 IoT 측정기기 설치', cover: '/images/cases/env-filter-adsorb2/cover.jpg',
    summary: '충북 증평군 — 소규모 4·5종 여과·흡착시설 2대 사업장에 대기배출 IoT 측정기기를 설치·연동한 사례입니다.' },
  { slug: 'env-filter-adsorb4', domain: 'environment', category: '설치', date: '2025. 04. 02',
    title: '여과·흡착시설 4대 + 배출시설 8대 설치', cover: '/images/cases/env-filter-adsorb4/cover.jpg',
    summary: '경기 화성시 — 여과·흡착시설 4대와 배출시설 8대 규모 사업장의 IoT 측정기기 설치 사례입니다.' },
  { slug: 'env-filter1', domain: 'environment', category: '설치', date: '2025. 03. 19',
    title: '여과집진시설 1대 + 배출시설 9대 설치', cover: '/images/cases/env-filter1/cover.jpg',
    summary: '충남 천안시 — 여과집진시설 1대·배출시설 9대 사업장에 대기배출방지 IoT 측정기기를 설치한 사례입니다.' },
  { slug: 'env-filter2', domain: 'environment', category: '설치', date: '2025. 05. 26',
    title: '여과집진시설 2대 + 배출시설 2대 설치', cover: '/images/cases/env-filter2/cover.jpg',
    summary: '충남 예산군 — 여과집진시설 2대·배출시설 2대 사업장의 IoT 측정기기 설치 사례입니다.' },
  { slug: 'env-filter3', domain: 'environment', category: '설치', date: '2025. 05. 19',
    title: '여과집진시설 3대 + 배출시설 4대 설치', cover: '/images/cases/env-filter3/cover.jpg',
    summary: '충남 천안시 동남구 — 레미콘 제조업(여과집진 3대·배출 4대) 사업장의 IoT 측정기기 설치 사례입니다.' },
  { slug: 'env-cyclone1', domain: 'environment', category: '설치', date: '2025. 03. 14',
    title: '원심력집진시설 1대 + 제재시설 4대 설치', cover: '/images/cases/env-cyclone1/cover.jpg',
    summary: '원심력집진시설 1대·제재시설 4대 사업장에 대기배출방지 IoT 측정기기를 설치한 사례입니다.' },
  { slug: 'env-cyclone-scrub', domain: 'environment', category: '설치', date: '2025. 03. 31',
    title: '원심력·세정집진 + 배출시설 2대 설치', cover: '/images/cases/env-cyclone-scrub/cover.jpg',
    summary: '충남 금산군 — 원심력집진·세정집진시설과 배출시설 2대 사업장의 IoT 측정기기 설치 사례입니다.' },
  { slug: 'env-absorb2', domain: 'environment', category: '설치', date: '2025. 04. 14',
    title: '흡수에 의한 시설 2대 사업장 설치', cover: '/images/cases/env-absorb2/cover.jpg',
    summary: '경기 화성시 — 전자집적회로 제조업(흡수에 의한 시설 2대) 사업장에 대기배출 IoT 측정기기를 설치한 사례입니다.' },
  { slug: 'env-adsorb1', domain: 'environment', category: '설치', date: '2025. 03. 24',
    title: '흡착에 의한 시설 1대 사업장 설치', cover: '/images/cases/env-adsorb1/cover.jpg',
    summary: '경기 파주시 — 흡착에 의한 시설 1대(목재 가구 제조업) 사업장에 대기배출방지 IoT 측정기기를 설치한 사례입니다.' },
  // 환경 — AS(사후관리) 사례
  { slug: 'env-as-internet', domain: 'environment', category: 'AS', date: '2025. 04. 17',
    title: 'IoT GW 인터넷 불량 데이터 미전송 조치', cover: '/images/cases/env-as-internet/cover.jpg',
    summary: 'IoT 게이트웨이 인터넷 불량으로 데이터가 미전송되던 문제를 현장 점검·조치한 AS 사례입니다.' },
  { slug: 'env-as-ph', domain: 'environment', category: 'AS', date: '2025. 04. 30',
    title: 'pH계 온도보상 모듈 교체 조치', cover: '/images/cases/env-as-ph/cover.jpg',
    summary: 'pH계 온도 보상용 모듈러를 교체 의뢰받아 조치한 AS 사례입니다.' },
  { slug: 'env-as-power', domain: 'environment', category: 'AS', date: '2025. 04. 11',
    title: '온열로 인한 전원 단절 조치', cover: '/images/cases/env-as-power/cover.jpg',
    summary: '현장 온열로 인해 발생한 전원 단절 문제를 진단·조치한 AS 사례입니다.' },
  { slug: 'env-as-sensor', domain: 'environment', category: 'AS', date: '2025. 05. 12',
    title: '차압계·전류계 오작동 조치', cover: '/images/cases/env-as-sensor/cover.jpg',
    summary: '차압계 및 전류계 오작동 문제를 현장에서 점검·조치한 AS 사례입니다.' },
  { slug: 'env-as-greenlink', domain: 'environment', category: 'AS', date: '2025. 05. 21',
    title: '차압센서 음수 표시·그린링크 통신 조치', cover: '/images/cases/env-as-greenlink/cover.jpg',
    summary: '차압센서 음수 표시 및 환경부 그린링크 통신 문제를 조치한 AS 사례입니다.' },
];

// 오시는 길 — evstech.co.kr(융합) 회사소개>오시는길 페이지 기준. 본사=정확한 구글맵 임베드(원본),
//   R&D센터=원본이 본사 지도를 재사용(원본 이슈)하므로 주소 기반 임베드로 대체(2026-07-31).
export const LOCATION = {
  title: '오시는 길',
  tagline: '본사와 R&D센터로 찾아오시는 길을 안내합니다.',
  offices: [
    {
      label: '본사',
      address: '충청남도 천안시 동남구 만남로9 237-15호',
      tel: '02-2039-3477, 3488',
      fax: '070-4324-3477', // [2026-10-11 사용자] 브로셔 pg8 FAX 가산
      mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3193.966861635031!2d127.1510880353801!3d36.81931399796677!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357b285b7ed13e73%3A0xcaf678b9517ee79a!2z7Lap7LKt64Ko64-EIOyynOyViOyLnCDrj5nrgqjqtawg66eM64Ko66GcIDkgMjM3LTE17Zi4!5e0!3m2!1sko!2skr!4v1744010225978!5m2!1sko!2skr',
    },
    {
      label: 'R&D센터',
      address: '서울시 구로구 부광로 96-5 구로에이스캠프 429호', // [2026-10-11] 브로셔 pg8 대조 오타교정: 구로에스캠프→구로에이스캠프
      tel: '02-9658-7279',
      mapQuery: '서울 구로구 부광로 96-5 구로에이스캠프',
    },
  ],
};

export const COMPANY: CompanyInfo = {
  name: '주식회사 이브이에스',
  hqAddress: '충청남도 천안시 동남구 만남로9 237-15호',
  rndAddress: '서울시 구로구 부광로 96-5 구로에이스캠프 429호',
  tel: '02-2039-3477, 3488',
  email: 'wwwkie@evstech.kr',
  bizNo: '287-81-03068',
};

// [2026-09-06 사용자 교정] D안 6메뉴 정합 — 헤더·홈과 동일하게 제품·안전·환경·도입사례·고객지원·회사소개.
//   기존 옛 IA(사업소개·제품소개·자료·회사 4컬럼)는 헤더가 6메뉴로 바뀐 뒤에도 안 바뀌어 불일치였음 → D융합안 푸터 스펙대로 교체.
//   회사소개=후순위(dim). 링크 실연결은 Phase 3(현재 href='#').
// [2026-10-11 사용자] 푸터 메뉴/서브를 실제 NAV(기획서 v7 최신)와 전수 대조해 '완전 동기화'.
//   구 푸터는 구버전 라벨(안전 단말기/SaaS/가스검침/인증6기관/인재상 등)·설치안내 컬럼 누락 등 다수 불일치 → NAV 기준으로 재작성.
//   라벨·순서·경로 모두 NAV.children과 1:1(아래 FOOT_LINKS도 동일 경로로 갱신). 근거: [[evs-footer-nav-consistency-todo]]
export const FOOTER_COLS: FooterColumn[] = [
  { title: '제품', links: ['전체 제품', 'EVS-G200 게이트웨이', '환경 계측기 4종', 'AI IoT 체감온도계', '모니터·TV 연결 중계기'] },
  { title: '안전', links: ['안전 솔루션', '체감온도 통합관제 플랫폼', '온열질환 예방', '한랭작업 관리', '밀폐공간 가스안전'] },
  { title: '환경', links: ['환경 IoT 솔루션', 'IoT 게이트웨이 제조', 'IoT 설치사업', '배출·방지시설 안내', 'GreenLink 전송'] },
  { title: '설치안내', links: ['도입 절차', '현장실사·준비자료', '설치 후 운영'] },
  { title: '설치 사례', links: ['환경 설치사례', '안전 설치사례', 'A/S·개선사례', '주요 거래처·실적'] },
  { title: '고객지원', links: ['실시간 상담', '문의하기', '내 문의', 'A/S 신청·현황', 'FAQ', '자료실', '뉴스·소식'] },
  { title: '회사소개', links: ['인사말·회사개요', '연혁·조직도', '인증·기술·연구소', '오시는 길'] },
];

// ── 회사소개 서브페이지(단일 페이지 + 앵커 섹션) 구성 (2026-08-02) ──
//   사용자 방향: 자식별 별도 페이지 X. /company 한 페이지에 인사말·조직도… 를 "섹션"으로 두고
//   헤더 서브메뉴 클릭 시 해당 섹션으로 부드럽게 스크롤. 섹션 id는 헤더 NAV(to: '/company#id')와 1:1.
//   디자인 기준 = evs.com/applications/corporate-events (히어로 정지이미지 + 중앙정렬 콘텐츠 + reveal).
// ── 사업소개 ② IoTGW설치 (/business/install) — 단일 페이지 + 4개 앵커 섹션 ──
//   레이아웃/스크롤 = IoTGW제조업(=about-evs 방식). 데이터 = evstech /igi·/ef·/its/·/installation 원문(2026-08-02).
//   섹션 id 는 헤더 서브메뉴 to: '/business/install#id' 와 1:1.
export const INSTALL_SECTIONS = [
  { id: 'igi', label: '방지시설의 정의' },
  { id: 'ef', label: '배출시설' },
  { id: 'its', label: '설치대상 기준' },
  { id: 'installation', label: '설치사례' },
] as const;

export const INSTALL = {
  hero: {
    slogan: '실시간으로 관리하는 소규모 대기배출시설',
    sub: 'IoT 측정기기 부착 의무 대상 · 방지시설 원격 모니터링',
  },
  // ① 방지시설의 정의 — 소규모 대기배출시설 방지시설 6종(시행규칙 [별표 9의2]).
  igi: {
    lead: '방지시설이란?',
    intro: '규모가 작은 공장, 사업장 등에서 배출되는 대기 오염 물질을 정화하거나 제거하는 데 사용되는 장치들로, 일반적으로 비용 효율적이고 관리가 용이한 설비들이 적용됩니다. 소규모 대기배출시설은 대기환경보전법 제2조 및 시행규칙 6조에 따라 일정 기준을 초과하는 대기오염 물질을 배출하지 않기 위해 방지시설을 설치해야 합니다.',
    facilitiesNote: '소규모 대기배출시설 방지시설 사물인터넷 측정기기 부착대상 (시행규칙 [별표 9의2] · 6가지 시설)',
    facilities: [
      { icon: 'cyclone', name: '원심력 집진시설', industry: '곡물업 등', principle: '원심력(전처리용)', pollutant: '입자상', iot: '(송풍시설) 전류계',
        desc: '원심력집진시설(사이클론 집진기)은 공기 중의 먼지나 입자를 원심력을 이용해 제거하는 장치입니다. 먼지 입자가 포함된 공기를 원형 챔버로 회전시켜, 무거운 입자가 원심력에 의해 벽으로 밀려나고 중력에 의해 아래로 떨어지게 하는 방식으로 작동합니다.' },
      { icon: 'filter', name: '여과 집진시설', industry: '자동차수리업 등', principle: '여과포 필터(Filter bag)', pollutant: '입자상', iot: '(방지시설) 차압계·온도계 · (송풍시설) 전류계',
        desc: '여과집진시설은 필터를 통해 공기 중의 먼지와 미세 입자를 제거하는 장치입니다. 섬유·직물 필터로 공기가 통과할 때 먼지 입자가 필터에 걸려 분리되며, 미세한 먼지까지 제거할 수 있어 효율성이 높습니다.' },
      { icon: 'adsorb', name: '흡착에 의한 시설', industry: '자동차수리업 등', principle: '활성탄 흡착', pollutant: '가스상', iot: '(방지시설) 차압계·온도계 · (송풍시설) 전류계',
        desc: '흡착에 의한 시설은 공기 중의 유해 가스나 증기를 흡착제로 제거하는 장치입니다. 활성탄·제올라이트 같은 다공성 물질을 흡착제로 사용해 유해 물질을 물리·화학적으로 흡착합니다. 가스 상태 오염 물질 처리에 적합합니다.' },
      { icon: 'electro', name: '전기 집진시설', industry: '섬유제조 시설 등', principle: '직류 고전압', pollutant: '입자상', iot: '(방지시설) 전류계 · (송풍시설) 전류계',
        desc: '전기집진시설은 공정에서 발생하는 미세먼지·분진을 전기적 힘으로 제거하는 장치입니다. 고전압으로 먼지 입자에 전하를 부여한 뒤, 반대 전하 전극판에 부착시켜 공기 중에서 분리·제거합니다.' },
      { icon: 'wet', name: '세정 집진시설', industry: '도금업 등', principle: '스크러버를 활용한 물분사', pollutant: '입자상', iot: '(펌프전류) 전류계 · (송풍시설) 전류계',
        desc: '세정집진시설은 미세 먼지·유해 물질을 물로 제거하는 장치입니다. 공기 중 입자를 물방울과 접촉시켜 제거하며, 미세먼지 제거뿐 아니라 유해 가스를 흡수해 환경오염을 줄입니다.' },
      { icon: 'absorb', name: '흡수에 의한 시설', industry: '도금업 등', principle: '반건식반응을 활용한 중금속류 등 흡수', pollutant: '가스상', iot: '(방지시설) pH계 · (펌프전류) 전류계 · (송풍시설) 전류계',
        desc: '흡수에 의한 시설은 가스 상태의 오염 물질을 제거하기 위해 액체 흡수제를 사용하는 장치입니다. 산업 공정에서 발생하는 유해 가스를 선택적으로 흡수하여 대기 중으로 배출되지 않도록 막아줍니다.' },
    ],
  },
  // ② 배출시설
  ef: {
    lead: '배출시설이란?',
    intro: '산업 활동이나 기타 인간 활동으로 대기 중에 오염 물질을 배출하는 시설을 의미합니다. 주로 공장·발전소·제조업체의 장비나 기계가 이에 해당하며, 연소·화학 반응·물리적 변화를 통해 대기 중에 오염 물질을 방출합니다.',
    pollutants: ['미세먼지', '질소산화물(NOx)', '황산화물(SOx)', '휘발성 유기화합물(VOCs)', '일산화탄소(CO)'],
    grades: [
      { g: '1종', v: '연간 80톤 이상' },
      { g: '2종', v: '연간 20톤 이상 ~ 80톤 미만' },
      { g: '3종', v: '연간 10톤 이상 ~ 20톤 미만' },
      { g: '4종', v: '연간 2톤 이상 ~ 10톤 미만' },
      { g: '5종', v: '연간 2톤 미만' },
    ],
    gradesNote: '대기오염물질 발생량의 연간 합계 기준. IoT 부착 의무 대상은 소규모 사업장인 4종·5종.',
  },
  // ③ 설치대상 기준
  its: {
    lead: '설치대상 기준',
    intro: '대기환경보전법 제17조에 따라 소규모 대기배출사업장(4종·5종)은 사물인터넷(IoT) 측정기기 부착 의무 대상입니다. 이 조항은 2022년 개정으로, 연간 배출량은 낮지만 실시간 모니터링이 필요한 사업장에 대한 규제를 포함하도록 변경되었습니다.',
    highlights: [
      { icon: 'calendar', title: '설치 시기', desc: '기존 4·5종 사업장은 2025년 6월 30일까지 IoT 측정기기 부착 완료' },
      { icon: 'support', title: '설치비 지원', desc: '환경부가 IoT 설치비용의 약 90%를 지원하여 설치를 촉진' },
      { icon: 'remote', title: '설치 목적', desc: '방지시설 가동 상태·배출가스 농도를 실시간 확인, 현장 방문 없이 원격 관리' },
    ],
    devices: [
      { fac: '원심력집진시설', emit: '전류계', prev: '전류계' },
      { fac: '세정집진시설', emit: '전류계', prev: '전류계' },
      { fac: '여과집진시설', emit: '전류계', prev: '전류계 · 차압계 · 온도계' },
      { fac: '전기집진시설', emit: '전류계', prev: '전류계' },
      { fac: '흡수에 의한 시설', emit: '전류계', prev: '전류계 · pH계' },
      { fac: '흡착에 의한 시설', emit: '전류계', prev: '전류계 · 차압계 · 온도계' },
    ],
    industries: [
      { name: '도장업', desc: '페인트 도장 공정에서 발생하는 휘발성 유기화합물(VOCs) 배출' },
      { name: '인쇄업', desc: '잉크·용제를 사용하는 인쇄 과정의 오염물질 관리' },
      { name: '금속 가공업', desc: '용접·절단·가공 공정의 미세먼지 및 대기오염물질' },
      { name: '섬유·염색업', desc: '염료·화학약품을 사용하는 제조·염색 공정의 오염물질' },
      { name: '제조업', desc: '화학물질 사용·연소 과정을 거치는 소규모 제조업체' },
    ],
  },
  // ④ 설치사례 — 이미지 기반(주요거래처·설치/AS 사례). 사진은 차후 반영.
  installation: {
    lead: '설치사례',
    intro: '이브이에스는 다양한 업종의 소규모 대기배출 사업장에 IoT 게이트웨이와 측정기기를 설치·운영하고 있으며, 안정적인 사후관리(A/S)로 신뢰를 쌓아가고 있습니다.',
  },
};

// ── 사업소개 ① IoT GW 제조업 (/business/igm) ──
//   레이아웃/애니메이션 기준 = evs.com/company/about-evs(이미지↔텍스트 교차 블록 + 스크롤 리빌).
//   데이터·사진 = evstech.co.kr /igm 원문 verbatim. 사진은 evstech img_igm_*.jpg → webp.
//   인증/프로토콜 등은 EVS 스타일 SVG 요소로 재디자인(2026-08-02).
export const IGM = {
  hero: {
    slogan: '환경과 산업을 잇는 IoT 게이트웨이',
    sub: '한국환경공단 승인·확인 · 전송률 우수 제조사',
  },
  intro: {
    lead: '이브이에스는 환경 보호와 산업 발전을 동시에 고려하는 혁신적인 사물인터넷(IoT) 게이트웨이 제조사입니다.',
    body: '최근 스마트 산업 환경에서는 IoT 기술의 발전이 가속화되고 있으며, 이를 통해 다양한 기기와 시스템의 효율적인 연결 및 관리가 가능해졌습니다. 이브이에스는 환경적인 요인을 고려한 자체 기술 개발과, 산업적 요구사항을 충족하는 고성능 제품을 제공하며, 사업적 성장을 이끌어가는 회사로 자리매김하고 있습니다.',
  },
  blocks: [
    {
      image: '/images/business/igm/igm-10.webp', side: 'left' as const,
      eyebrow: 'MANUFACTURING',
      heading: 'IoT GW 제조업',
      paragraphs: [
        '이브이에스는 환경적인 요인을 고려한 자체 기술 개발과, 산업적 요구사항을 충족하는 고성능 제품을 제공하며, 사업적 성장을 이끌어가는 회사로 자리매김하고 있습니다.',
      ],
    },
    {
      image: '/images/business/igm/igm-20.webp', side: 'right' as const,
      eyebrow: 'APPLICATION',
      // ★[2026-09-10 사용자 교정] 원문은 환경 전용("대기오염·환경 모니터링")이었으나,
      //   게이트웨이는 안전(폭염·체감온도/온열질환)·환경(대기배출) 현장을 모두 연계하는 공통 백본이라 두 축 포함으로 확장.
      //   근거: memory/evs-identity-safety-and-env.md.
      heading: '안전·환경 현장을 잇는 실시간 IoT 모니터링',
      paragraphs: [
        '이브이에스의 사물인터넷 게이트웨이는 산업 현장의 온열질환 예방(폭염·체감온도 감시)부터 대기오염물질 배출 관리까지, 안전과 환경 전반의 현장 데이터를 실시간으로 수집·연계합니다. 한국환경공단의 승인·확인을 받았으며, 기술적 노력과 성능을 인정받았습니다.',
        'IoT 게이트웨이는 실시간으로 데이터를 수집하고 분석하여, 현장의 위험과 환경 변화를 빠르게 감지하고 관리할 수 있도록 합니다. 이는 기업들이 안전하고 지속 가능한 운영을 실현하는 데 중요한 역할을 합니다.',
      ],
    },
  ],
  // 핵심 성과/특장점 — EVS SVG 아이콘 4종으로 재디자인.
  features: [
    { icon: 'cert', title: '한국환경공단 승인·확인', desc: '환경 보호를 위한 기술력과 성능을 인정받은 승인·확인 제품' },
    { icon: 'rate', title: '전송률 우수 제조사', desc: '2024년 2/4분기 전송률 우수 GW 제조사 선정' },
    { icon: 'realtime', title: '실시간 데이터 수집·분석', desc: '환경 변화를 빠르게 감지하고 관리' },
    { icon: 'facility', title: '소규모배출시설 관리', desc: '소규모 대기환경 사업장 맞춤 솔루션 제공' },
  ],
  product: {
    image: '/images/business/igm/igm-30.webp', side: 'left' as const,
    eyebrow: 'PRODUCTS & SOLUTIONS',
    heading: 'IoT GW 제품과 솔루션',
    protocols: ['V1.0', 'V2.6.4', 'V3.0', '복수형', '교대형'],
    paragraphs: [
      '이브이에스가 제조한 IoT 게이트웨이는 위 프로토콜 및 시스템에 인증을 마쳤으며, 전송률 우수 제조사로도 선정되었습니다. 이브이에스에서 제조한 IoT GW는 소규모배출시설관리시스템 구축 분야에서 안정적으로 사용할 수 있는 고성능 제품을 제공합니다.',
      '이브이에스는 환경적 책임과 산업적 요구를 동시에 충족하는 혁신적인 IoT 게이트웨이 제조사입니다. 한국환경공단 승인·확인을 받은 제품과, 전송률 우수 제조사로 인정받은 성과를 바탕으로, 환경 보호와 산업 효율성 향상에 기여하며, 소규모 대기환경 사업장에서 요구하는 사양에 맞춰 다양한 솔루션을 제공합니다.',
    ],
  },
  future: {
    image: '/images/business/igm/igm-future.webp',
    eyebrow: 'FUTURE',
    heading: '이브이에스의 미래',
    text: '이브이에스는 앞으로도 지속적인 연구 개발을 통해, 글로벌 시장에서 경쟁력을 강화하고, 더 나은 환경 관리와 산업 발전을 위해 노력할 것입니다.',
  },
};

// ── 제품소개 (/products) ─────────────────────────────────────────────────
//   ★목업 = viaportal.evs.com (다크 #1C212E/#222834 · 라이트 #F1F3F7 · 시안 #00AEEF · 가는제목 300 · 각진모서리).
//     기존 사이트와 배경/색/레이아웃이 다른 것은 사용자 의도(2026-08-02).
//   ★콘텐츠·이미지 = evstech.co.kr /products 전수 실측(2026-08-02). 실제 회사 제품 사진 → 원본 보존(contain, 무왜곡).
//     매핑 실측: img_igm_30.png=게이트웨이 / img_products_10~40=전류계·차압계·온도계·pH계.
//   사양은 evstech 원문 verbatim(CPU/Memory/통신/센서/전원/Display). 미기재 항목은 추가하지 않음.
export const PRODUCT_SECTIONS = [
  { id: 'gateway', label: 'IoT Gateway' },
  { id: 'instruments', label: 'IoT 계측기기' },
] as const;

export const PRODUCT_PAGE = {
  hero: {
    // 목업 히어로 = 풀높이 배경이미지 + 중앙 가는 제목. 영문 대제목 + 국문 보조.
    // [2026-10-08 사용자/기획서 v7 2-1] 대표제목(슬로건) = 문서 요구 문구 그대로. 제품 페이지는 환경+안전 모두 포괄.
    slogan: 'Products',
    sloganKo: '환경과 산업안전을 연결하는 EVS 제품',
    sub: '안전 단말기부터 IoT 게이트웨이·환경 계측기까지, EVS의 전 제품을 한곳에서 비교하고 바로 상담·견적까지 연결합니다.', // [2026-10-11 사용자] 서브 슬로건 풍성화
    image: PAGE_HERO_IMAGE, // 전 페이지 공통 히어로(단일 소스). 위 PAGE_HERO_IMAGE 참고.
  },
  intro: {
    lead: '현장에서 검증된 EVS 제품군',
    body: '이브이에스는 소규모 대기배출시설의 실시간 원격 모니터링을 위한 IoT 게이트웨이와 계측기기를 자체 공급합니다. 게이트웨이 전 모델이 한국환경공단 승인·확인을 완료했습니다.',
  },
  // IoT Gateway — EVS-G200 (단수/복수/교대형). 사양은 evstech 원문 그대로.
  gateway: {
    tag: 'IoT GATEWAY',
    name: 'EVS-G200',
    approval: '환경공단 승인 완료',
    image: '/images/products/gateway-g200.png', // [2026-10-08 사용자] 실제 EVS-G200 제품샷으로 교체(구 gateway.png). 정사각 포커싱 크롭(흰배경). [[evs-image-box-design]]
    desc: '계측기기의 데이터를 수집·표시하고, 광대역(Ethernet·LTE)과 근거리(RS-485·232) 통신으로 관제센터에 실시간 전송하는 IoT 게이트웨이입니다. 7인치 터치 LCD로 현장에서 측정값을 즉시 확인할 수 있습니다.',
    variants: [
      { name: 'EVS-G200', type: '단수형', note: '환경공단 승인 완료' },
      { name: 'EVS-G200D', type: '복수형', note: '환경공단 승인 완료' },
      { name: 'EVS-G200', type: '교대형', note: '환경공단 승인 완료' },
    ],
    // evstech 원문 사양 verbatim(추가·추정 없음).
    specs: [
      { k: 'CPU', v: 'INTEL CHERRY TRAIL QUAD CORE 1.8GHz' },
      { k: 'Memory', v: '4G 32Bit DDR3' },
      { k: 'Storage', v: '32G' },
      { k: 'O/S', v: 'WINDOWS 10 IoT' },
      { k: '광대역 통신', v: 'Ethernet, LTE(VPN)' },
      { k: '근거리 통신', v: 'RS-485, RS-232' },
      { k: '전류센서', v: '4~20mA 전류센서(차압/pH/전류계) 10CH · 온도센서 1CH · 디지털입력 RS-485 확장보드(55CH)' },
      { k: '전원', v: 'AC 220V' },
      { k: 'Display', v: '1080P 7" LCD 터치스크린' },
    ],
  },
  // IoT 계측기기 4종 — evstech 실측 이미지 매핑. 설명은 각 계측기의 측정 대상(정의적 사실)만 간결히.
  instruments: {
    lead: 'IoT 계측기기',
    body: '게이트웨이(EVS-G200)와 4~20mA로 연동되는 4종의 계측기기로 배출·방지시설의 상태를 실시간 계측합니다.',
    items: [
      { name: '전류계', en: 'Current Meter', image: '/images/products/product-1.jpg', desc: '분할형 CT 센서로 송풍·배출 전류를 측정하여 방지시설의 가동 상태를 감지합니다.' },
      { name: '차압계', en: 'Differential Pressure', image: '/images/products/product-2.jpg', desc: '집진·여과 시설의 전후단 압력차(Pa)를 측정합니다.' },
      { name: '온도계', en: 'Temperature', image: '/images/products/product-3.jpg', desc: 'RTD 방식 온도센서로 배출가스·설비의 온도를 계측합니다.' },
      { name: 'pH계', en: 'pH Meter', image: '/images/products/product-4.jpg', desc: '흡수·세정 시설의 수소이온농도(pH)를 측정합니다.' },
    ],
  },
};

// ── 고객지원 (/support) ───────────────────────────────────────────────────
//   목업 = evs.com/contact ("Let's get in touch", 문의 폼 중심) 실측.
//   콘텐츠 = evstech 견적문의 실측(영업1/영업2 전화·이메일). 디자인 = 기존 리엑트(라이트+네이비+시안, SubPageHero).
//   오시는 길 = 기존 LOCATION(본사 천안 · R&D 서울 구로) 재사용 → ContentStore.location.
//   ⚠ 교차검증 불일치: LOCATION.rnd 전화(02-9658-7279) ↔ 견적문의 영업2(02-6958-7279). 라이브 실측값(6958) 채택, 추후 확인 필요.
export const SUPPORT_SECTIONS = [
  { id: 'inquiry', label: '견적문의' },
  { id: 'resources', label: '자료실' },
  { id: 'location', label: '오시는 길' },
] as const;

export const SUPPORT = {
  hero: {
    slogan: "Let's get in touch",
    sloganKo: '무엇을 도와드릴까요?',
    sub: '제품 견적·설치·기술 지원 문의를 남겨주시면 담당 영업팀이 신속히 답변드립니다.',
  },
  intro: {
    lead: '언제든 문의해 주세요',
    body: 'IoT 게이트웨이·계측기기 도입, 소규모 대기배출시설 원격 모니터링 구축, 설치 후 기술 지원까지 — 아래 양식으로 문의를 남기시거나 영업팀으로 직접 연락 주시면 신속하게 안내해 드립니다.',
  },
  // evstech 견적문의 페이지 실측 연락처(2026-08-02).
  contacts: [
    { team: '영업 1', tel: '02-2039-3477', email: 'wwwkie@evstech.kr' },
    { team: '영업 2', tel: '02-6958-7279', email: 'kjh770519@evstech.kr' },
  ],
  // 문의 폼(프론트 데모 — 백엔드 미연동). 목업 evs.com/contact 필드를 국문화.
  form: {
    subjects: ['제품 견적 문의', 'IoT 게이트웨이 · 계측기기 도입', '방지시설 원격 모니터링 설치', '기술 지원 / A/S', '기타 문의'],
    privacyNote: '문의 접수 및 답변을 위해 이름·연락처·이메일을 수집하며, 목적 달성 후 파기합니다.',
  },
  // 자료실 — 목업(사용자 지시 2026-08-02). 실제 파일 미등록 → 다운로드 비활성('준비 중'). 추후 파일 연결.
  resources: {
    lead: '자료실',
    body: '제품 카탈로그·매뉴얼·인증 자료를 제공합니다. 자료는 순차적으로 등록될 예정입니다.',
    items: [
      { title: 'EVS-G200 제품 카탈로그', type: 'PDF', desc: 'IoT 게이트웨이 사양·구성 안내' },
      { title: 'IoT 게이트웨이 사용자 매뉴얼', type: 'PDF', desc: '설치·운영·유지보수 가이드' },
      { title: '계측기기 설치 가이드', type: 'PDF', desc: '전류·차압·온도·pH 센서 설치 방법' },
      { title: '한국환경공단 승인·확인 서류', type: 'PDF', desc: '소규모 대기배출시설 관리 승인·확인' },
      { title: '회사소개서', type: 'PDF', desc: '이브이에스 기업·사업 소개' },
    ],
  },
};

// 회사소개 히어로 오버랩 슬로건 — evs.com "We create return on emotion" 대응.
//   EVS(실시간 대기환경 IoT 모니터링 / '맑은 하늘' 미션) 브랜드 슬로건으로 생성(2026-08-02, 사용자 지시).
//   제목("회사소개")은 breadcrumb로만 남기고, 히어로 대형 텍스트는 이 슬로건. 국문 보조라인 포함.
export const COMPANY_HERO = {
  slogan: 'We create a cleaner tomorrow', // 영문 대제목=웹 자체 문구(브로셔 미수록, grep 확인). 사용자 지시로 유지.
  // [2026-10-11 사용자] 국문 서브를 브로셔 공식 태그라인(251128 pg2·pg8)으로 교체 → 환경 편중('맑은 공기') 해소, 정체성(IoT·산업·환경·안전) 포괄.
  //   구: '실시간으로 잇는 맑은 공기, 더 나은 내일'. 근거: [[evs-brochure-251128-data]] · [[evs-identity-safety-and-env]].
  sloganKo: 'IoT통신 기술을 통해 산업 현장과 데이터를 하나로 연결합니다.',
};

export const COMPANY_SECTIONS = [
  { id: 'greeting', label: '인사말' },
  { id: 'org', label: '조직도' },
  { id: 'history', label: '주요연혁' },
  { id: 'talent', label: '인재상' },
  { id: 'location', label: '오시는 길' },
] as const;

// 조직도 — evstech.co.kr /organization (img_organization_10.jpg) 구조 전수 실측(2026-08-02).
//   원본은 하늘색 그라데이션 원형/알약 이미지. EVS 컨셉(네이비+시안, 심플·현대)으로 벡터 재해석해 렌더.
//   구조: 경영 → [경영지원팀(인사/총무/행정) · 영업본부 · 기술연구개발부] → 각 본부 하위 팀 → 팀별 업무.
export const ORG_CHART = {
  root: '경영',
  divisions: [
    { label: '경영지원팀', teams: [{ label: '인사 · 총무 · 행정', items: [] as string[] }] },
    { label: '영업본부', teams: [
      { label: '영업관리 / 기술영업팀', items: ['영업관리', '기술영업', '현장실사'] },
      { label: '일반 영업팀', items: ['신규영업'] },
    ]},
    { label: '기술연구개발부', teams: [
      { label: '기술팀', items: ['현장설치', 'A/S', '현장실사', '현장교육'] },
      { label: '개발팀 (연구소)', items: ['하드웨어', '소프트웨어', 'Version Upgrade'] },
    ]},
  ],
};

// 주요연혁 — evstech.co.kr /history (img_history_10.jpg) 타임라인 전수 실측(2026-08-02).
//   원본은 중앙선 좌우교차 타임라인 이미지. EVS 컨셉으로 좌측 레일 모던 타임라인으로 벡터 재해석.
//   최신순(2024→2020). 텍스트 verbatim.
export const HISTORY = [
  { year: '2024', events: [
    { month: '8월', text: '한국 환경공단 24년 2/4분기 전송률 우수 GW 제조사 선정' },
    { month: '8월', text: "한국 환경공단 IoT 통신 프로토콜 '교대' 인증" },
    // [2026-10-11 사용자] 브로셔(251128 pg8) 대조 — 연혁 누락분 가산. 근거: [[evs-brochure-251128-data]]
    { month: '6월', text: "한국 환경공단 IoT 통신 프로토콜 '복수' 인증" },
  ]},
  { year: '2023', events: [
    { month: '6월', text: '서울지점 설립' },
    { month: '9월', text: '한국 환경공단 IoT 통신 프로토콜 3.0 인증' },
  ]},
  { year: '2021', events: [
    { month: '3월', text: '한국환경관리공단 통신프로토콜 개발 인증' },
  ]},
  { year: '2020', events: [
    { month: '9월', text: '한국환경관리공단 소규모방지시설 시범사업 참여' },
  ]},
];

// 인재상 — evstech.co.kr /rightpeople 원문 전수(2026-08-02). 원본 아이콘(png 3개)은 EVS 라인 SVG로 재해석.
//   창의(CREATIVITY)·신의(TRUST) 2대 핵심가치. 텍스트 verbatim.
export const TALENT = {
  eyebrow: 'TALENT',
  headline: '신의와 창의를 바탕으로 성장과 변화를 추구하여\n개척자 정신으로 미래를 준비하는 이브이에스人',
  ideology: "이브이에스는 ‘지속적인 이익창출을 통하여 인류사회에 기여하는 기업경영’이라는 이념 아래 나아갑니다.",
  values: [
    {
      icon: 'creativity', en: 'CREATIVITY', ko: '창의', hanja: '創意',
      keywords: ['문제의식이 높은 사람', '정보력이 뛰어난 사람', '과감히 도전하는 사람'],
      desc: '뛰어난 정보력과 창의적인 사고방식을 바탕으로 높은 목표에 도전하며 실행을 통해 완수하는 인재',
    },
    {
      icon: 'trust', en: 'TRUST', ko: '신의', hanja: '信義',
      keywords: ['믿음을 주는 사람', '책임감이 있는 사람', '예의를 갖춘 사람'],
      desc: '주인이라는 책임감으로 매사에 임하여 사회관계 및 조직생활에 믿음과 예의를 바탕으로 자신의 본분을 다하는 사람',
    },
  ],
};

// 인사말 — evstech.co.kr 인사말 원문(http://www.evstech.co.kr/onbr/brd/onct/?pgk=/greetings/onct) 그대로 반영(2026-08-02).
//   headline은 본문 첫 문장에서 도출("당사는 환경과 산업의 혁신적 변화를 이끌고 있는 …IoT 게이트웨이 제조사입니다").
//   body/lead/signature는 원문 verbatim(원문 오타 "제공하는 하도록"→"제공하도록"만 교정).
//   ★[2026-09-10 사용자 교정] 마지막 구절 "모든 환경 모니터링 솔루션"은 환경 전용 표현이라,
//     EVS 실제 정체성(안전=폭염/체감온도·온열질환 + 환경=대기배출 IoT, 두 축)에 맞춰
//     "안전과 환경을 아우르는 모니터링 솔루션"으로 확장(evstech 원문에서 의도적 이탈). 근거: memory/evs-identity-safety-and-env.md.
export const GREETING = {
  eyebrow: '인사말',
  headLead: '환경과 산업의 ',
  headAccent: '혁신적 변화',
  headTail: '를 이끄는 IoT 게이트웨이 제조사',
  lead: '안녕하세요. 우선, 당사 홈페이지에 방문해 주신 여러분께 깊은 감사의 말씀을 드립니다.',
  body: [
    '당사는 환경과 산업의 혁신적 변화를 이끌고 있는 사물인터넷(IoT) 게이트웨이 제조사입니다. 창립 후 짧은 기간동안 눈부신 성장을 이뤄 가는 중이며, 다양한 환경적 요구에 발맞춰 최첨단 기술을 제공하고 있습니다.',
    '항상 ‘도전’과 ‘혁신’을 기업의 핵심 가치로 삼으며 경험 많은 임직원들과 함께 최상의 시너지를 만들어가며 『이브이에스』 만의 뛰어난 팀워크와 열정적인 에너지는 날마다 회사의 급성장을 이끌고 있습니다. 고객 여러분의 신뢰를 바탕으로, 앞으로도 빠르게 변화하는 시장 속에서 끊임없는 혁신을 이루어, 단순히 제품을 공급하는 것을 넘어서, 고객이 필요로 하는 안전과 환경을 아우르는 모니터링 솔루션을 제공하도록 노력하겠습니다.',
  ],
  signature: 'EVS CEO 배상',
};
